import { adjacentDeckSlideIndex, deckSlideHref } from "@/lib/deckNavigation";
import { AI_FOUNDATION_ANCHOR_ALIASES } from "@/data/aiWorkflowFoundations";
import { ElevateFoundations } from "@/components/training/ElevateFoundations";
import { ElevateSwotLibrary } from "@/components/training/ElevateSwotLibrary";
import { ElevateShowcaseExample } from "@/components/training/ElevateShowcaseExample";
import { ElevateRecordSkill } from "@/components/training/ElevateRecordSkill";
import { ElevateAgentSetup } from "@/components/training/ElevateAgentSetup";
import { ToolLoop } from "@/components/training/AIToolLoop";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Menu,
  PlayCircle,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import SEO from "@/components/SEO";
import { GuidePromptSlide } from "@/components/training/SlideGuide";
import {
  S2P_ELEVATE_CORE_PROGRAM as S2P_ELEVATE_PROGRAM,
  ELEVATE_CHIEF_OF_STAFF_SLIDE,
  ELEVATE_CHIEF_OF_STAFF_STEP,
} from "@/data/s2pElevateCoreProgram";
import {
  type S2PElevateProgram,
  type S2PElevatePractice,
  type S2PElevateSection,
  type S2PElevateShowcase,
  type S2PElevateSlide,
  type S2PElevateTone,
} from "@/data/s2pElevateAI";
import { S2P_ELEVATE_VIDEOS, type S2PElevateVideo } from "@/data/s2pElevateVideos";
import { trackEvent } from "@/utils/posthog";

export interface S2PElevateDeckConfig {
  storageNamespace: string;
  analyticsVariant: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string[];
  brand?: "serve2perform" | "tech-leaders";
  robots?: string;
  coverTitle?: string;
  coverEyebrow?: string;
  coverAudience?: string | null;
  coverSessionTitles?: string[];
  showCoverLogo?: boolean;
  showNavigationFooter?: boolean;
  showContentsMenu?: boolean;
  contentsEyebrow?: string;
  contentsTitle?: string;
  sessionNoun?: string;
  brandHref?: string;
  slideBasePath?: string;
  brandLinkLabel?: string;
  exitHref?: string;
  exitLabel?: string;
  finishCtaHref?: string;
  finishCtaLabel?: string;
  finishEyebrow?: string;
  finishBulletsLabel?: string;
  guideHref?: string | null;
  guideLabel?: string;
  magicPromptHref?: string;
  showcaseSupportLabel?: string;
  showcaseSourceLabel?: string;
}

interface S2PElevateDeckContextValue {
  program: S2PElevateProgram;
  config: S2PElevateDeckConfig;
  renderSlide?: (slide: S2PElevateSlide) => ReactNode;
  renderContents?: (navigation: { activeSlideId: string; goToSlideId: (id: string) => void }) => ReactNode;
  navigationSlideIds?: readonly string[];
  navigationLabel?: string;
}

const S2PElevateDeckContext = createContext<S2PElevateDeckContextValue | null>(null);

const useS2PElevateDeck = () => {
  const value = useContext(S2PElevateDeckContext);
  if (!value) throw new Error("S2PElevateDeck components must render inside S2PElevateDeckContext.");
  return value;
};

const ProgramVideoDialog = ({
  video,
  slideId,
  onClose,
}: {
  video: S2PElevateVideo;
  slideId: string;
  onClose: () => void;
}) => {
  const { config } = useS2PElevateDeck();
  const isTechLeaders = config.brand === "tech-leaders";

  return (
    <div
      className="absolute inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-black/90 p-4 backdrop-blur-lg sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`s2p-video-title-${slideId}`}
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className={`w-full max-w-5xl overflow-hidden rounded-3xl border shadow-2xl ${isTechLeaders ? "border-[#FF7420]/45 bg-[#080808]" : "border-[#5FC6D8]/45 bg-[#07172F]"}`}>
        <div className="flex items-start justify-between gap-4 border-b border-white/10 p-5 sm:p-6">
          <div>
            <p className={`text-xs font-extrabold uppercase tracking-[0.18em] ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#5FC6D8]"}`}>Related video · {video.duration}</p>
            <h2 id={`s2p-video-title-${slideId}`} className="s2p-display mt-2 text-2xl font-semibold uppercase text-white sm:text-3xl">{video.title}</h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-300 sm:text-base">{video.relevance}</p>
          </div>
          <button type="button" onClick={onClose} className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-white/15 text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 ${isTechLeaders ? "focus-visible:ring-[#FF7420]" : "focus-visible:ring-[#5FC6D8]"}`} aria-label="Close video"><X className="h-5 w-5" /></button>
        </div>
        <div className="aspect-video bg-black">
          <iframe
            className="h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&autoplay=1`}
            title={video.title}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 sm:px-6">
          <p className="text-xs text-slate-400">From the Tech Leaders AI YouTube channel</p>
          <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className={`inline-flex min-h-10 items-center gap-2 rounded-full px-4 text-sm font-bold ${isTechLeaders ? "bg-[#FF7420] text-white hover:bg-[#E4691E]" : "bg-[#5FC6D8] text-[#07172F] hover:bg-[#8DDBE8]"}`}>Watch on YouTube <ExternalLink className="h-4 w-4" /></a>
        </div>
      </div>
    </div>
  );
};

const TONE_CLASSES: Record<NonNullable<S2PElevateDeckConfig["brand"]>, Record<S2PElevateTone, string>> = {
  serve2perform: {
    navy: "bg-[#0A1F44] text-white",
    blue: "bg-[#0047FF] text-white",
    paper: "bg-[#FBF8F4] text-[#111827]",
    cloud: "bg-[#F4F7FB] text-[#111827]",
  },
  "tech-leaders": {
    navy: "bg-[#080808] text-white",
    blue: "bg-[#1F2A44] text-white",
    paper: "bg-[#FBF8F4] text-[#111827]",
    cloud: "bg-[#F4F1EC] text-[#111827]",
  },
};

const DECK_BRANDS = {
  serve2perform: {
    courseLabel: "Serve2Perform ELEVATE",
    providerName: "Serve2Perform",
    providerUrl: "https://serve2perform.com",
    headerLogoSrc: "/serve2perform-logo.png",
    darkLogoSrc: "/serve2perform-logo.png",
    logoAlt: "Serve2Perform",
    defaultExitHref: "https://serve2perform.com",
    defaultExitLabel: "Serve2Perform",
  },
  "tech-leaders": {
    courseLabel: "Longhand AI Program",
    providerName: "Tech Leaders",
    providerUrl: "https://www.technical-leaders.com",
    headerLogoSrc: "/transparent-bg-logo.png",
    darkLogoSrc: "/orange-logo.png",
    logoAlt: "Tech Leaders",
    defaultExitHref: "/ai-30/program",
    defaultExitLabel: "Longhand AI",
  },
} as const;

const getDeckBrand = (config: S2PElevateDeckConfig) =>
  DECK_BRANDS[config.brand ?? "serve2perform"];

const MAGIC_PROMPT_SLIDE_ID = "outcome-first-prompt-format";

const TextWithFillInBlanks = ({ text }: { text: string }) => {
  const parts = text.split("___");
  return parts.flatMap((part, index) => (
    index === parts.length - 1
      ? [part]
      : [part, <span key={`fill-in-blank-${index}`} className="s2p-fill-in-blank" aria-label="blank">&nbsp;</span>]
  ));
};

const STATUS_CLASSES = {
  yes: "border-emerald-300 bg-emerald-50 text-emerald-800",
  partial: "border-amber-300 bg-amber-50 text-amber-900",
  no: "border-rose-300 bg-rose-50 text-rose-800",
};

const GEAR_PROBLEM_ITEMS = [
  { number: 4, title: "Loops", body: "Recurring outcome · continuous system", accent: "#047857", tint: "#E1F2E5" },
  { number: 3, title: "Agents", body: "Multi-step objective · delegated run", accent: "#0E7490", tint: "#DCF3F6" },
  { number: 2, title: "Assistant", body: "One finished deliverable · sustained help", accent: "#A16207", tint: "#FCF4DA" },
  { number: 1, title: "Chat", body: "One question or idea · quick burst", accent: "#B45309", tint: "#FCEBDE" },
  { number: 0, title: "Human judgement drives every gear", body: "Sets direction · reviews results", accent: "#3E4A63", tint: "#EDF0F5" },
] as const;

const LONGHAND_GEAR_ITEMS = [
  {
    number: 1,
    title: "Chat",
    body: "Answer a question",
    color: "#F0A46A",
    accent: "#B45309",
    rotor: "chat",
    desktopTransform: "translate(130 265) scale(1.55)",
    mobileTransform: "translate(115 262) scale(1.2)",
    numberSize: 24,
    connector: "M220 265 L550 249 L600 249",
    titleY: 244,
    bodyY: 273,
    tint: "#FCEBDE",
  },
  {
    number: 2,
    title: "Assistant",
    body: "Finish a deliverable",
    color: "#EBCB55",
    accent: "#A16207",
    rotor: "assistant",
    desktopTransform: "translate(274 178) scale(1.275)",
    mobileTransform: "translate(224 194) scale(1)",
    numberSize: 25,
    connector: "M348 178 L550 184 L600 184",
    titleY: 179,
    bodyY: 208,
    tint: "#FCF4DA",
  },
  {
    number: 3,
    title: "Agents",
    body: "Complete an objective",
    color: "#5FC6D8",
    accent: "#0E7490",
    rotor: "agents",
    desktopTransform: "translate(393 103) scale(1.05)",
    mobileTransform: "translate(316 134) scale(.84)",
    numberSize: 27,
    connector: "M454 103 L550 119 L600 119",
    titleY: 114,
    bodyY: 143,
    tint: "#DCF3F6",
  },
  {
    number: 4,
    title: "Loops",
    body: "Sustain an outcome",
    color: "#77C58A",
    accent: "#047857",
    rotor: "loops",
    desktopTransform: "translate(487 42) scale(.85)",
    mobileTransform: "translate(388 82) scale(.68)",
    numberSize: 29,
    connector: "M535 42 L565 54 L600 54",
    titleY: 49,
    bodyY: 78,
    tint: "#E1F2E5",
  },
] as const;

const createCourseSchema = (program: S2PElevateProgram, config: S2PElevateDeckConfig) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  name: `${program.curriculum.course.title}: ${getDeckBrand(config).courseLabel}`,
  description: config.seoDescription,
  provider: {
    "@type": "Organization",
    name: getDeckBrand(config).providerName,
    url: getDeckBrand(config).providerUrl,
  },
  hasCourseInstance: {
    "@type": "CourseInstance",
    courseMode: "online",
    courseWorkload: `PT${program.totalMinutes}M`,
  },
});

const copyText = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Some embedded or permission-restricted browsers expose the API but reject writes.
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("Copy command was rejected.");
};

const getSlideIndexFromHash = (hash: string, slides: S2PElevateSlide[]) => {
  const requested = decodeURIComponent(hash.replace(/^#/, ""));
  if (!requested) return -1;
  const currentId = requested === "ai-comparison-evidence" ? "ai-swot-library" : requested;
  const exact = slides.findIndex((slide) => slide.id === (AI_FOUNDATION_ANCHOR_ALIASES[currentId] ?? currentId));
  if (exact >= 0) return exact;
  // Removed mindset lessons open the next retained slide in their session.
  const nextMindsetSlideIds: Record<string, string> = {
    "elevate-communication-support": "elevate-change-metaphors",
    "elevate-t-thinking": "elevate-sponsor-and-champion",
    "elevate-pyramid-recommendation": "elevate-pilot-charter",
  };
  if (nextMindsetSlideIds[currentId]) return slides.findIndex((slide) => slide.id === nextMindsetSlideIds[currentId]);
  // Decks that removed the tool-fit slide open their next teaching example.
  if (currentId === "ai-swot-library") return slides.findIndex((slide) => ["showcase-inbox-agent", "showcase-forecast-inventory"].includes(slide.id));
  if (currentId === "browser-extension-setup") return slides.findIndex((slide) => slide.id === (slides.some((item) => item.id === "showcase-logged-in-browser") ? "showcase-logged-in-browser" : "agent-setup-workspace"));
  // Retired activity links open the following tool-choice discussion.
  if (currentId === "ai-workflow-pilot" || currentId === "level-fit-huddle") return slides.findIndex((slide) => slide.id === "showcase-debrief");
  // Keep old Session 4 links useful in the two decks with the setup sequence.
  const setupAliases: Record<string, string> = {
    "agent-access-ladder": "agent-setup-workspace",
    "agent-team-boundary": "chief-of-staff-operating-brief",
    "ten-eighty-ten": "agent-run-now-or-later",
    "steps-done-edge-cases": "chief-of-staff-operating-brief",
    "browser-safety-model": "agent-test-exception",
    "session-3-connections-tools": "chief-of-staff-operating-brief",
    "ai-tool-loop": "agent-run-on-demand",
    "ai-watcher-workflow": "agent-schedule-instructions",
    "session-3-prompt-example": "agent-schedule-instructions",
    "governance-operating-controls": "agent-manage-runs",
    "generate-install": "agent-setup-workspace",
  };
  if (setupAliases[currentId]) return slides.findIndex((slide) => slide.id === setupAliases[currentId]);
  const numeric = Number.parseInt(requested, 10);
  return Number.isFinite(numeric) && numeric > 0
    ? Math.min(numeric - 1, slides.length - 1)
    : -1;
};

const Logo = ({ compact = false, darkSurface = false }: { compact?: boolean; darkSurface?: boolean }) => {
  const { config } = useS2PElevateDeck();
  const brand = getDeckBrand(config);
  const isTechLeaders = config.brand === "tech-leaders";

  return (
    <div className={`flex items-center rounded-lg ${isTechLeaders && darkSurface ? "" : "bg-white"} ${compact ? (isTechLeaders ? "px-1 py-1" : "px-3 py-2") : "px-4 py-3"}`}>
      <img
        src={darkSurface ? brand.darkLogoSrc : brand.headerLogoSrc}
        alt={brand.logoAlt}
        className={compact ? (isTechLeaders ? "h-auto w-36 sm:w-44" : "h-auto w-32 sm:w-40") : "h-auto w-44"}
      />
    </div>
  );
};

const PromptCopyButton = ({ label, value, slideId }: { label: string; value: string; slideId: string }) => {
  const { config } = useS2PElevateDeck();
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await copyText(value);
      setCopied(true);
      trackEvent("S2P Elevate AI Prompt Copied", {
        slide: slideId,
        prompt: label,
        program_variant: config.analyticsVariant,
      });
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex min-h-10 flex-shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#0A1F44] hover:bg-[#E7EEFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5FC6D8]"
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

const PromptPanel = ({ label, value, slideId, compact = false }: { label: string; value: string; slideId: string; compact?: boolean }) => (
  <div data-prompt-panel className="overflow-hidden rounded-2xl border border-white/15 bg-[#07172F] text-white shadow-2xl shadow-[#0A1F44]/20">
    <div className={`flex items-center justify-between gap-4 border-b border-white/10 px-4 sm:px-5 ${compact ? "py-2" : "py-3"}`}>
      <p className="text-xs font-extrabold uppercase tracking-[0.15em] text-[#9DB6FF] sm:text-sm lg:text-base">{label}</p>
      <PromptCopyButton label={label} value={value} slideId={slideId} />
    </div>
    <pre className={`overflow-auto whitespace-pre-wrap px-4 font-mono text-slate-100 sm:px-5 ${compact ? "max-h-[30svh] py-3 text-sm leading-6 sm:text-sm lg:text-base" : "max-h-[46svh] py-5 text-sm leading-7 sm:text-base lg:text-lg"}`}>
      {value}
    </pre>
  </div>
);

const PracticePanel = ({ practice, slideId }: { practice: S2PElevatePractice; slideId: string }) => (
  <>
  {practice.decisionPause && <p data-decision-pause className="mt-3 rounded-lg border border-[#9DAFCB] bg-[#E7EEFF] px-4 py-3 text-sm leading-relaxed text-[#0A1F44]"><strong>Pause and decide · 20 seconds: </strong>{practice.decisionPause}</p>}
  <details data-practice-panel className="mt-3 rounded-lg border border-[#9DAFCB] bg-white text-[#0A1F44]">
    <summary className="cursor-pointer px-4 py-3 text-sm font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0047FF]">{practice.title}</summary>
    <div className="space-y-3 border-t border-[#C8D3E5] p-3 sm:p-4">
      <p className="text-xs leading-snug text-slate-600">Illustrative workshop sample. Replace it with approved inputs for your own Job; the expected result is a review target.</p>
      <PromptPanel label={practice.promptLabel ?? "Starter prompt + sample inputs"} value={`${practice.prompt}\n\nILLUSTRATIVE SAMPLE INPUTS\n${practice.sampleInput}`} slideId={slideId} compact />
      {practice.followUp && (
        <div data-handoff-follow-up className="space-y-3">
          <p className="text-sm leading-relaxed">{practice.followUp.instructions}</p>
          <PromptPanel label={practice.followUp.label} value={practice.followUp.prompt} slideId={slideId} compact />
        </div>
      )}
      {practice.offline && (
        <details data-offline-practice className="rounded-lg border border-[#9DAFCB]">
          <summary className="cursor-pointer px-3 py-3 text-sm font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0047FF]">No tool access? Review saved outputs</summary>
          <div className="space-y-3 border-t border-[#C8D3E5] p-3">
            <p className="text-sm leading-relaxed">{practice.offline.instructions}</p>
            <p className="text-xs leading-relaxed text-slate-600">Authored workshop examples, not captured AI runs. Record “review exercise”; leave AI runtime, usage, and unmeasured savings unknown.</p>
            {practice.offline.outputs.map((output) => <figure key={output.label} className="rounded border border-[#C8D3E5] bg-slate-50 p-3"><figcaption className="text-xs font-bold">{output.label}</figcaption><pre tabIndex={0} className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-sm leading-relaxed focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0047FF]">{output.text}</pre></figure>)}
          </div>
        </details>
      )}
      {practice.decisionPause ? (
        <details data-review-target className="border-l-2 border-[#0047FF] pl-3">
          <summary className="cursor-pointer py-2 text-sm font-bold text-[#0047FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0047FF]">After your decision: reveal the review target</summary>
          <p className="mt-1 whitespace-pre-line text-sm leading-relaxed">{practice.expectedOutput}</p>
        </details>
      ) : <div className="border-l-2 border-[#0047FF] pl-3">
        <h2 className="text-xs font-extrabold uppercase tracking-wide text-[#0047FF]">Expected sample result</h2>
        <p className="mt-1 whitespace-pre-line text-sm leading-relaxed">{practice.expectedOutput}</p>
      </div>}
      {practice.sources && <ul className="flex flex-wrap gap-x-4 gap-y-2 text-xs">{practice.sources.map((source) => <li key={source.href}><a href={source.href} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#0047FF] underline underline-offset-2">{source.label}</a></li>)}</ul>}
    </div>
  </details>
  </>
);

const EDITORIAL_ACCENTS = ["#E4691E", "#0047FF", "#0E7490", "#047857", "#D59A00", "#6D5BD0"] as const;
const EDITORIAL_ACCENTS_DARK = ["#FF9A5B", "#5FC6D8", "#C9D7FF", "#7DE2B8", "#FBBF24", "#C4B5FD"] as const;
const editorialAccent = (index: number, isLight: boolean) => {
  const palette = isLight ? EDITORIAL_ACCENTS : EDITORIAL_ACCENTS_DARK;
  return palette[index % palette.length];
};

const EditorialEyebrow = ({ children, isLight }: { children?: string; isLight: boolean }) => {
  if (!children) return null;
  return (
    <p className={`flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.22em] sm:text-sm lg:text-base ${isLight ? "text-[#E4691E]" : "text-[#5FC6D8]"}`}>
      <span className={`h-0.5 w-9 sm:w-12 ${isLight ? "bg-[#E4691E]" : "bg-[#5FC6D8]"}`} aria-hidden="true" />
      {children}
    </p>
  );
};

const EditorialHeader = ({ slide, isLight, compact = false }: { slide: S2PElevateSlide; isLight: boolean; compact?: boolean }) => {
  const { config } = useS2PElevateDeck();
  const keepHeadlineWordsIntact = slide.id === "three-layers";
  const headlineSize = keepHeadlineWordsIntact
    ? config.analyticsVariant === "rockline"
      ? "text-[clamp(2.75rem,4.7vw,5.5rem)]"
      : "text-[clamp(1.75rem,12cqw,6.25rem)]"
    : compact
      ? "text-[clamp(2.5rem,4.35vw,5.15rem)]"
      : "text-[clamp(3rem,5.35vw,6.25rem)]";

  return (
    <header data-editorial-header className={`max-w-[1500px] ${keepHeadlineWordsIntact ? "[container-type:inline-size]" : ""}`}>
      <EditorialEyebrow isLight={isLight}>{slide.eyebrow}</EditorialEyebrow>
      <h1 className={`s2p-editorial mt-3 font-bold leading-[0.96] tracking-[-0.045em] ${headlineSize} ${keepHeadlineWordsIntact ? "break-normal [overflow-wrap:normal]" : "[overflow-wrap:anywhere]"} ${isLight ? "text-[#111827]" : "text-white"}`}>
        {slide.title}
      </h1>
      {slide.lead && <p className={`mt-4 max-w-6xl text-base font-medium leading-relaxed sm:text-lg lg:text-xl xl:text-2xl ${isLight ? "text-slate-600" : "text-slate-300"}`}>{slide.lead}</p>}
    </header>
  );
};

const shouldRenderSourceLink = (slide: S2PElevateSlide) =>
  Boolean(slide.sourceUrl && !slide.sourceUrl.startsWith("/") && slide.sourceLabel !== "Open the source slide");

const EditorialSourceLink = ({ slide, isLight }: { slide: S2PElevateSlide; isLight: boolean }) => {
  if (!shouldRenderSourceLink(slide)) return null;
  return (
    <a href={slide.sourceUrl!} target="_blank" rel="noopener noreferrer" className={`inline-flex w-fit items-center gap-2 text-sm font-bold sm:text-base lg:text-lg ${isLight ? "text-[#0047FF]" : "text-[#9DB6FF]"}`}>
      {slide.sourceLabel ?? "Open source"} <ExternalLink className="h-4 w-4" />
    </a>
  );
};

const EditorialSections = ({ sections, isLight, compact = false, hideLabels = false }: { sections: S2PElevateSection[]; isLight: boolean; compact?: boolean; hideLabels?: boolean }) => (
  <div data-editorial-sections className={`border-y ${isLight ? "divide-slate-200 border-slate-200" : "divide-white/15 border-white/15"}`}>
    {sections.map((section, index) => (
      <article key={`${section.title}-${index}`} className={`grid gap-3 border-b py-3 last:border-b-0 sm:items-baseline sm:gap-6 ${hideLabels ? "grid-cols-1 sm:grid-cols-[minmax(190px,.62fr)_1.1fr]" : "grid-cols-[minmax(112px,.38fr)_1fr] sm:grid-cols-[minmax(140px,.38fr)_minmax(190px,.62fr)_1.1fr]"} ${compact ? "sm:py-3.5" : "sm:py-5"} ${isLight ? "border-slate-200" : "border-white/15"}`}>
        {hideLabels ? null : (
          <span className={`${section.label ? "s2p-display text-lg uppercase tracking-[0.02em] sm:text-xl lg:text-2xl" : "s2p-editorial text-3xl sm:text-4xl"} break-words font-bold leading-[.95]`} style={{ color: editorialAccent(index, isLight) }}>
            {section.label ?? String(index + 1).padStart(2, "0")}
          </span>
        )}
        <h2 className={`s2p-display text-xl font-semibold uppercase leading-tight ${compact ? "sm:text-xl lg:text-2xl" : "sm:text-2xl lg:text-3xl"} ${isLight ? "text-[#0A1F44]" : "text-white"}`}>{section.title}</h2>
        <div className={hideLabels ? "col-start-1 sm:col-start-2" : "col-start-2 sm:col-start-3"}>
          <p className={`text-base leading-relaxed ${compact ? "sm:text-base lg:text-lg" : "sm:text-lg lg:text-xl"} ${isLight ? "text-slate-600" : "text-slate-300"}`}>{section.body}</p>
          {section.prompt && <p className={`mt-2 border-l-2 pl-3 font-mono text-sm leading-relaxed sm:text-base lg:text-lg ${isLight ? "border-[#0047FF] text-slate-700" : "border-[#5FC6D8] text-slate-200"}`}>{section.prompt}</p>}
        </div>
      </article>
    ))}
  </div>
);

const EditorialList = ({ items, isLight, start = 1, compact = false }: { items: string[]; isLight: boolean; start?: number; compact?: boolean }) => (
  <ol data-editorial-list className={`border-y ${isLight ? "divide-slate-200 border-slate-200" : "divide-white/15 border-white/15"}`}>
    {items.map((item, index) => (
      <li key={item} className={`grid grid-cols-[54px_1fr] items-start gap-4 border-b last:border-b-0 ${compact ? "py-3" : "py-4 sm:py-5"} ${isLight ? "border-slate-200" : "border-white/15"}`}>
        <span className="s2p-editorial text-3xl font-bold leading-none sm:text-4xl" style={{ color: editorialAccent(index, isLight) }}>{String(start + index).padStart(2, "0")}</span>
        <span className={`text-base font-semibold leading-relaxed sm:text-lg lg:text-xl xl:text-2xl ${isLight ? "text-slate-700" : "text-slate-200"}`}>{item}</span>
      </li>
    ))}
  </ol>
);

const EditorialCallouts = ({ slide, isLight }: { slide: S2PElevateSlide; isLight: boolean }) => {
  if (!slide.note && !slide.warning) return null;
  return (
    <div data-editorial-callouts className="grid gap-3">
      {slide.note && <p className={`border-l-4 p-4 text-sm font-medium leading-relaxed sm:text-base lg:text-lg ${isLight ? "border-[#0047FF] bg-[#E7EEFF]/65 text-[#0A1F44]" : "border-[#5FC6D8] bg-white/[0.06] text-slate-200"}`}><strong>Remember:</strong> {slide.note}</p>}
      {slide.warning && <p className={`border-l-4 p-4 text-sm font-medium leading-relaxed sm:text-base lg:text-lg ${isLight ? "border-[#D59A00] bg-amber-50 text-amber-950" : "border-[#FBBF24] bg-amber-400/10 text-amber-100"}`}><strong>Guardrail:</strong> {slide.warning}</p>}
    </div>
  );
};

const ToolToTeamThinkingSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const [toolThinking, teamThinking] = slide.sections ?? [];
  return (
    <div
      data-layout="ten-x-tool-to-team"
      className="relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] overflow-hidden bg-[#020817] px-5 text-white sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
    >
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          backgroundImage: "radial-gradient(circle at 82% 20%, rgba(56,189,248,.13), transparent 28%), radial-gradient(circle at 8% 92%, rgba(255,116,32,.14), transparent 28%), linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)",
          backgroundSize: "auto, auto, 4vw 4vw, 4vw 4vw",
        }}
      />
      <span className="pointer-events-none absolute -bottom-[24%] right-[2%] font-sans text-[clamp(18rem,38vw,44rem)] font-extrabold leading-[.78] tracking-[-.12em] text-[#FF7420]/10" aria-hidden="true">1</span>

      <div className="relative mx-auto flex min-h-full w-full max-w-[1760px] flex-col justify-center py-5 sm:py-7">
        <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-[#38BDF8] sm:text-sm lg:text-base">
          <span className="h-0.5 w-12 bg-[#38BDF8]" />{slide.eyebrow}
        </p>
        <h1 className="s2p-editorial mt-5 max-w-[15ch] text-[clamp(3rem,5.4vw,6.35rem)] font-semibold leading-[.98] tracking-[-0.05em] text-white">
          {slide.title}
        </h1>

        <div className="mt-7 grid items-stretch gap-4 lg:grid-cols-[1fr_auto_1fr] lg:gap-7">
          {toolThinking && (
            <article className="border border-white/20 bg-white/[0.04] p-5 sm:p-7 lg:p-8">
              <h2 className="s2p-editorial text-3xl font-semibold leading-tight tracking-[-0.035em] sm:text-4xl lg:text-5xl">{toolThinking.title}</h2>
              <p className="mt-4 text-lg leading-relaxed text-[#B8C5D8] sm:text-xl lg:text-2xl">{toolThinking.body}</p>
            </article>
          )}

          <div className="grid place-items-center text-[#FF7420]" aria-hidden="true">
            <ArrowRight className="h-12 w-12 rotate-90 stroke-[1.5] lg:h-16 lg:w-16 lg:rotate-0" />
          </div>

          {teamThinking && (
            <article className="border border-[#FF7420]/60 bg-[#FF7420]/[0.08] p-5 sm:p-7 lg:p-8">
              <h2 className="s2p-editorial text-3xl font-semibold leading-tight tracking-[-0.035em] sm:text-4xl lg:text-5xl">{teamThinking.title}</h2>
              <p className="mt-4 text-lg leading-relaxed text-[#B8C5D8] sm:text-xl lg:text-2xl">{teamThinking.body}</p>
            </article>
          )}
        </div>

        {slide.quote && (
          <div className="mt-4 grid gap-1 border-t border-white/15 pt-3 sm:grid-cols-[auto_1fr] sm:items-baseline sm:gap-5">
            <p className="s2p-editorial text-xl font-semibold text-[#FF9A5B] sm:text-2xl lg:text-3xl">{slide.quote}</p>
            {slide.note && <p className="text-base leading-relaxed text-[#B8C5D8] sm:text-lg lg:text-xl">{slide.note}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

const AITeamManagementModelSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const principles = slide.sections ?? [];
  return (
    <div
      data-layout="ten-x-management-model"
      className="relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] overflow-hidden bg-[#FAF8F5] px-5 text-[#171F2F] sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
    >
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          backgroundImage: "radial-gradient(circle at 8% 16%, rgba(255,116,32,.09), transparent 22%), linear-gradient(90deg, transparent 49.8%, rgba(10,31,68,.045) 50%, transparent 50.2%)",
        }}
      />
      <span className="pointer-events-none absolute bottom-0 left-0 h-1 w-[24%] bg-[#FF7420]" aria-hidden="true" />

      <div className="relative mx-auto grid min-h-full w-full max-w-[1760px] items-center gap-8 py-6 lg:grid-cols-[1.25fr_.75fr] lg:gap-[6.5%] lg:py-8">
        <div>
          <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-[#C8550A] sm:text-sm lg:text-base">
            <span className="h-0.5 w-12 bg-[#C8550A]" aria-hidden="true" />{slide.eyebrow}
          </p>
          <h1 className="s2p-editorial mt-5 max-w-[13ch] text-[clamp(3rem,5.5vw,6.5rem)] font-semibold leading-[.98] tracking-[-0.05em] text-[#171F2F]">
            {slide.title}
          </h1>
        </div>

        <ol className="grid gap-4 sm:gap-5 lg:gap-6">
          {principles.map((principle, index) => (
            <li key={principle.title} className="grid grid-cols-[2.25rem_1fr] items-start gap-3 sm:grid-cols-[2.75rem_1fr] sm:gap-4">
              <span className="pt-0.5 text-lg font-extrabold tabular-nums text-[#FF7420] sm:text-xl lg:text-2xl">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span>
                <strong className="block text-2xl font-extrabold leading-tight text-[#171F2F] sm:text-3xl lg:text-4xl">{principle.title}</strong>
                <span className="mt-1 block text-lg leading-snug text-[#344258] sm:text-xl lg:text-2xl">{principle.body}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

const AITeamOwnershipQuestionSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const accent = "own?";
  const prompt = slide.title.endsWith(accent) ? slide.title.slice(0, -accent.length) : slide.title;
  return (
    <div
      data-layout="ten-x-engagement-question"
      className="relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] items-center justify-center overflow-hidden bg-[#FAF8F5] px-5 text-center text-[#171F2F] sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
    >
      <span className="pointer-events-none absolute bottom-0 left-0 h-1 w-[60%] bg-[#FF7420]" aria-hidden="true" />
      <h1 className="s2p-editorial max-w-[14ch] text-[clamp(3rem,5.35vw,6.5rem)] font-semibold leading-[1.02] tracking-[-0.05em]">
        {prompt}<span className="text-[#FF7420]">{accent}</span>
      </h1>
    </div>
  );
};

const ChiefOfStaffOperatingBriefSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const briefAnatomy = [
    ["Outcome", "One ranked brief: what matters now, what is blocked, and what can wait."],
    ["Sources", "Only the approved calendars, messages, tasks, files, and commitments you name."],
    ["Decision", "Sort each item into Do, Delegate, or Defer and explain why."],
    ["Evidence", "Include a direct source link, blocker, next step, and owner."],
    ["Stop", "Ask before external action, record changes, unclear ownership, or conflicting facts."],
  ] as const;

  return (
    <div data-layout="chief-of-staff-live-brief" className="relative -mx-5 grid min-h-full w-[calc(100%+2.5rem)] items-center gap-6 overflow-hidden bg-[#020817] px-5 py-4 text-white sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:grid-cols-[minmax(0,.68fr)_minmax(0,1.32fr)] lg:gap-8 lg:px-12 xl:gap-12">
      <div className="min-w-0">
        <EditorialHeader slide={slide} isLight={false} compact />
        <div className="mt-5 flex flex-col gap-3">
          <EditorialCallouts slide={slide} isLight={false} />
          <EditorialSourceLink slide={slide} isLight={false} />
        </div>
      </div>

      <section className="min-w-0 overflow-hidden rounded-2xl border border-white/15 bg-[#07172F] text-white shadow-2xl shadow-[#020817]/30" aria-label={slide.promptLabel ?? "Chief of Staff starter brief"}>
        <div className="flex items-center justify-between gap-4 border-b border-white/10 px-4 py-3 sm:px-5">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#9DB6FF] sm:text-sm">Full prompt included</p>
            <p className="mt-1 text-sm font-semibold text-slate-300 sm:text-base">Project the anatomy. Copy the complete Skill.</p>
          </div>
          {slide.prompt && <PromptCopyButton label={slide.promptLabel ?? "Chief of Staff starter brief"} value={slide.prompt} slideId={slide.id} />}
        </div>
        <ol className="divide-y divide-white/10 px-4 sm:px-5">
          {briefAnatomy.map(([label, detail], index) => (
            <li key={label} className="grid grid-cols-[2.2rem_minmax(96px,.3fr)_minmax(0,1fr)] items-baseline gap-3 py-3 sm:grid-cols-[2.6rem_minmax(120px,.3fr)_minmax(0,1fr)] sm:gap-4">
              <span className="s2p-editorial text-2xl font-bold leading-none text-[#5FC6D8] sm:text-3xl">{String(index + 1).padStart(2, "0")}</span>
              <strong className="text-sm uppercase tracking-[0.08em] text-white sm:text-base lg:text-lg">{label}</strong>
              <span className="text-sm leading-relaxed text-slate-300 sm:text-base lg:text-lg">{detail}</span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
};

const ConceptEditorialSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const { config } = useS2PElevateDeck();
  const isLight = slide.tone === "paper" || slide.tone === "cloud";
  const hasDenseSections = (slide.sections?.length ?? 0) > 4;
  const isCompact = hasDenseSections
    || slide.id === "chief-of-staff-job"
    || slide.id === "session-5-metric-loop-source"
    || slide.id === "session-5-metric-loop-improvement"
    || slide.id === "feedback-and-schedule";
  const isFullWidth = slide.id === "session-5-metric-loop-title";
  const hideSectionLabels = config.analyticsVariant === "rockline" && slide.id === "three-layers";
  return (
    <div
      data-layout="editorial-concept"
      data-compact={isCompact ? "true" : undefined}
      data-full-width={isFullWidth ? "true" : undefined}
      className={`mx-auto grid w-full max-w-[1760px] lg:items-center ${isFullWidth ? "min-h-full content-center gap-7 py-5 lg:py-7" : isCompact ? "h-full min-h-0 gap-5 py-3 lg:grid-cols-[minmax(0,.7fr)_minmax(0,1.3fr)] lg:gap-8 lg:py-4 xl:gap-9" : "min-h-full gap-7 py-5 lg:grid-cols-[minmax(0,.78fr)_minmax(0,1.22fr)] lg:gap-8 lg:py-7 xl:gap-12"}`}
    >
      <div className="min-w-0">
        <EditorialHeader slide={slide} isLight={isLight} compact={isCompact} />
        {slide.quote && <blockquote className={`s2p-editorial border-l-4 pl-5 font-semibold leading-snug ${isCompact ? "mt-3 text-xl sm:text-2xl lg:text-3xl" : "mt-5 text-2xl sm:text-3xl lg:text-4xl"} ${isLight ? "border-[#E4691E] text-[#0A1F44]" : "border-[#5FC6D8] text-white"}`}><TextWithFillInBlanks text={slide.quote} /></blockquote>}
        <div className={`${isCompact ? "mt-3" : "mt-5"} flex flex-col gap-3`}>
          <EditorialCallouts slide={slide} isLight={isLight} />
          <EditorialSourceLink slide={slide} isLight={isLight} />
        </div>
      </div>
      {!isFullWidth && (
        <div className="min-w-0">
          {slide.sections && <EditorialSections sections={slide.sections} isLight={isLight} compact={isCompact} hideLabels={hideSectionLabels} />}
          {slide.bullets && <EditorialList items={slide.bullets} isLight={isLight} compact={slide.bullets.length > 4} />}
          {slide.prompt && <PromptPanel label={slide.promptLabel ?? "Copyable prompt"} value={slide.prompt} slideId={slide.id} />}
          {slide.practice && <PracticePanel practice={slide.practice} slideId={slide.id} />}
        </div>
      )}
    </div>
  );
};

const CompareEditorialSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const isLight = slide.tone === "paper" || slide.tone === "cloud";
  const sections = slide.sections ?? [];
  return (
    <div data-layout="editorial-compare" className="mx-auto flex min-h-full w-full max-w-[1760px] flex-col justify-center py-4 sm:py-5">
      <EditorialHeader slide={slide} isLight={isLight} compact />
      {slide.quote && <p className={`s2p-editorial mt-4 max-w-[1500px] text-2xl font-semibold sm:text-3xl lg:text-4xl ${isLight ? "text-[#0A1F44]" : "text-white"}`}>{slide.quote}</p>}

      <div className={`mt-6 grid border-y ${sections.length === 2 ? "lg:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-4"} ${isLight ? "border-slate-200" : "border-white/15"}`}>
        {sections.map((section, index) => (
          <article key={`${section.title}-${index}`} className={`relative py-4 sm:p-5 lg:p-6 ${index > 0 ? isLight ? "sm:border-l sm:border-slate-200" : "sm:border-l sm:border-white/15" : ""} ${index > 1 ? isLight ? "border-t border-slate-200 lg:border-t-0" : "border-t border-white/15 lg:border-t-0" : ""}`}>
            <div className="flex items-baseline justify-between gap-4">
              <span className="s2p-editorial text-6xl font-bold leading-none opacity-35 sm:text-7xl" style={{ color: editorialAccent(index, isLight) }}>{String(index + 1).padStart(2, "0")}</span>
              {section.label && <span className={`text-xs font-extrabold uppercase tracking-[0.16em] sm:text-sm ${isLight ? "text-slate-500" : "text-slate-400"}`}>{section.label}</span>}
            </div>
            <h2 className={`s2p-editorial mt-1 text-3xl font-bold leading-tight sm:text-4xl ${isLight ? "text-[#0A1F44]" : "text-white"}`}>{section.title}</h2>
            <p className={`mt-3 text-base leading-relaxed sm:text-lg lg:text-xl ${isLight ? "text-slate-600" : "text-slate-300"}`}>{section.body}</p>
            {section.prompt && <p className={`mt-3 border-l-2 pl-3 font-mono text-sm leading-relaxed sm:text-base lg:text-lg ${isLight ? "border-[#0047FF] text-slate-700" : "border-[#5FC6D8] text-slate-200"}`}>{section.prompt}</p>}
          </article>
        ))}
      </div>

      {slide.prompt && <div className="mt-5 max-w-6xl"><PromptPanel label={slide.promptLabel ?? "Try it"} value={slide.prompt} slideId={slide.id} /></div>}
      <div className="mt-4 flex flex-col gap-3"><EditorialCallouts slide={slide} isLight={isLight} /><EditorialSourceLink slide={slide} isLight={isLight} /></div>
    </div>
  );
};

const PromptEditorialSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const isLight = slide.tone === "paper" || slide.tone === "cloud";
  const isCompact = slide.id === "chief-of-staff-operating-brief";
  return (
    <div data-layout="editorial-prompt" data-compact={isCompact ? "true" : undefined} className={`mx-auto grid w-full max-w-[1760px] lg:items-center ${isCompact ? "h-full min-h-0 gap-5 py-3 lg:grid-cols-[minmax(0,.64fr)_minmax(0,1.36fr)] lg:gap-8 lg:py-4 xl:gap-9" : "min-h-full gap-7 py-5 lg:grid-cols-[minmax(0,.72fr)_minmax(0,1.28fr)] lg:gap-8 lg:py-7 xl:gap-12"}`}>
      <div className="min-w-0">
        <EditorialHeader slide={slide} isLight={isLight} compact />
        {slide.quote && <blockquote className={`s2p-editorial mt-5 border-l-4 pl-5 font-semibold leading-snug ${slide.id === "session-2-choose-job-prompt" ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl lg:text-4xl"} ${isLight ? "border-[#E4691E] text-[#0A1F44]" : "border-[#5FC6D8] text-white"}`}>{slide.quote}</blockquote>}
        <div className="mt-5 flex flex-col gap-3"><EditorialCallouts slide={slide} isLight={isLight} /><EditorialSourceLink slide={slide} isLight={isLight} /></div>
      </div>
      <div className="min-w-0 space-y-4">
        {slide.prompt && <PromptPanel label={slide.promptLabel ?? "Copyable prompt"} value={slide.prompt} slideId={slide.id} compact={slide.id === "session-2-job-examples"} />}
        {slide.sections && <EditorialSections sections={slide.sections} isLight={isLight} compact />}
      </div>
    </div>
  );
};

const DiscussionEditorialSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const isLight = slide.tone === "paper" || slide.tone === "cloud";
  const questions = slide.questions ?? [];
  const isCompact = slide.id === "qa-4";
  if (slide.debrief) return (
    <div data-layout="editorial-discussion" data-facilitated-debrief className="mx-auto flex min-h-full w-full max-w-[1600px] flex-col justify-center py-5">
      <EditorialHeader slide={slide} isLight={isLight} compact />
      <p className="mt-4 text-sm font-semibold">{slide.debrief.timing}</p>
      <div className={`mt-4 grid gap-5 border-y py-4 lg:grid-cols-2 lg:gap-10 ${isLight ? "border-slate-200" : "border-white/20"}`}>
        <div>
          <h2 className="text-sm font-extrabold uppercase tracking-wide">Show the work</h2>
          <p className="mt-2 text-base leading-relaxed">{slide.debrief.share}</p>
          <p className="mt-2 text-sm leading-relaxed">{slide.debrief.transfer}</p>
        </div>
        <div data-closing-commitment className="border-l-4 border-[#5FC6D8] pl-4">
          <h2 className="text-sm font-extrabold uppercase tracking-wide">Final 2 minutes · Your next test</h2>
          <p className="s2p-editorial mt-2 text-2xl font-semibold leading-snug sm:text-3xl">{slide.debrief.commitment}</p>
          <p className="mt-2 text-sm leading-relaxed">Keep this with your work. {slide.session === 5 ? "Choose a date to review what happened." : "Bring the result to the next session."}</p>
        </div>
      </div>
      {slide.practice && <PracticePanel practice={slide.practice} slideId={slide.id} />}
    </div>
  );
  return (
    <div data-layout="editorial-discussion" className="mx-auto flex min-h-full w-full max-w-[1760px] flex-col justify-center py-5 sm:py-7">
      <div className="grid gap-6 lg:grid-cols-[.72fr_1.28fr] lg:items-end lg:gap-10">
        <EditorialHeader slide={slide} isLight={isLight} compact />
        {slide.quote && <blockquote className={`s2p-editorial border-l-4 pl-5 text-3xl font-semibold leading-snug sm:text-4xl lg:text-5xl ${isLight ? "border-[#E4691E] text-[#0A1F44]" : "border-[#5FC6D8] text-white"}`}>{slide.quote}</blockquote>}
      </div>
      {questions.length > 0 && (
        <ol className={`${isCompact ? "mt-4" : "mt-6"} grid border-y sm:grid-cols-2 ${isLight ? "border-slate-200" : "border-white/15"}`}>
          {questions.map((question, index) => (
            <li key={question} className={`grid gap-4 border-b ${isCompact ? "grid-cols-[52px_1fr] py-3 sm:p-4" : "grid-cols-[64px_1fr] py-4 sm:p-5"} ${index % 2 === 1 ? isLight ? "sm:border-l sm:border-slate-200" : "sm:border-l sm:border-white/15" : ""} ${index >= questions.length - 2 ? "sm:border-b-0" : ""} ${isLight ? "border-slate-200" : "border-white/15"}`}>
              <span className={`s2p-editorial font-bold leading-none ${isCompact ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl"}`} style={{ color: editorialAccent(index, isLight) }}>{String(index + 1).padStart(2, "0")}</span>
              <span className={`s2p-editorial font-semibold leading-snug ${isCompact ? "text-lg sm:text-xl lg:text-2xl" : "text-xl sm:text-2xl lg:text-3xl"} ${isLight ? "text-[#0A1F44]" : "text-white"}`}>{question}</span>
            </li>
          ))}
        </ol>
      )}
      {slide.prompt && <div className="mt-5 max-w-6xl"><PromptPanel label={slide.promptLabel ?? "Carry sentence"} value={slide.prompt} slideId={slide.id} /></div>}
      {slide.bullets && <div className="mt-5"><EditorialList items={slide.bullets} isLight={isLight} /></div>}
      <div className={`${isCompact ? "mt-3" : "mt-4"} flex flex-col gap-3`}><EditorialCallouts slide={slide} isLight={isLight} /><EditorialSourceLink slide={slide} isLight={isLight} /></div>
      {slide.practice && <PracticePanel practice={slide.practice} slideId={slide.id} />}
    </div>
  );
};

const ActivityEditorialSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const isLight = slide.tone === "paper" || slide.tone === "cloud";
  const sequence = slide.sections ?? [];
  const list = sequence.length > 0 ? (slide.questions ?? []) : (slide.questions ?? slide.bullets ?? []);
  const isCompact = sequence.length > 3;
  return (
    <div data-layout="editorial-activity" data-agent-practice={slide.sourceIds?.includes("elevate.agent-setup.2026-10-09") || undefined} className="mx-auto grid min-h-full w-full max-w-[1760px] gap-7 py-5 lg:grid-cols-[minmax(0,.78fr)_minmax(0,1.22fr)] lg:items-center lg:gap-8 lg:py-7 xl:gap-12">
      <div className="min-w-0">
        <EditorialHeader slide={slide} isLight={isLight} compact />
        {slide.quote && <blockquote className={`s2p-editorial mt-5 border-l-4 pl-5 text-2xl font-semibold leading-snug sm:text-3xl lg:text-4xl ${isLight ? "border-[#E4691E] text-[#0A1F44]" : "border-[#5FC6D8] text-white"}`}><TextWithFillInBlanks text={slide.quote} /></blockquote>}
        <div className="mt-5 flex flex-col gap-3"><EditorialCallouts slide={slide} isLight={isLight} /><EditorialSourceLink slide={slide} isLight={isLight} /></div>
      </div>
      <div className="min-w-0 space-y-4">
        {sequence.length > 0 && <EditorialSections sections={sequence} isLight={isLight} compact={isCompact} />}
        {list.length > 0 && <EditorialList items={list} isLight={isLight} compact={list.length > 3} />}
        {slide.prompt && <PromptPanel label={slide.promptLabel ?? "Copyable prompt"} value={slide.prompt} slideId={slide.id} compact={isCompact} />}
        {slide.practice && <PracticePanel practice={slide.practice} slideId={slide.id} />}
      </div>
    </div>
  );
};

const OutcomesEditorialSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const { config } = useS2PElevateDeck();
  const isLight = slide.tone === "paper" || slide.tone === "cloud";
  const bullets = slide.bullets ?? [];
  const isCompact = (config.analyticsVariant === "rockline" && slide.id === "finish-line")
    || slide.id === "chief-of-staff-decision-rules";
  return (
    <div data-layout="editorial-outcomes" data-compact={isCompact ? "true" : undefined} className={`mx-auto grid w-full max-w-[1760px] lg:grid-cols-[.84fr_1.16fr] lg:items-center ${isCompact ? "h-full min-h-0 gap-5 py-3 lg:gap-10 lg:py-4" : "min-h-full gap-8 py-5 lg:gap-14 lg:py-7"}`}>
      <div>
        <EditorialHeader slide={slide} isLight={isLight} compact={isCompact} />
        {slide.quote && <blockquote className={`s2p-editorial font-semibold leading-snug ${isCompact ? "mt-3 text-2xl sm:text-3xl lg:text-4xl" : "mt-5 text-3xl sm:text-4xl lg:text-5xl"} ${isLight ? "text-[#0047FF]" : "text-[#5FC6D8]"}`}>{slide.quote}</blockquote>}
        <div className={`${isCompact ? "mt-3" : "mt-5"} flex flex-col gap-3`}><EditorialCallouts slide={slide} isLight={isLight} /><EditorialSourceLink slide={slide} isLight={isLight} /></div>
      </div>
      <div className="relative">
        <span className={`s2p-editorial pointer-events-none absolute -top-14 right-0 text-[9rem] font-bold leading-none opacity-[0.055] sm:text-[13rem] ${isLight ? "text-[#0047FF]" : "text-white"}`} aria-hidden="true">{String(bullets.length).padStart(2, "0")}</span>
        {bullets.length > 0 && <EditorialList items={bullets} isLight={isLight} compact={isCompact || bullets.length > 4} />}
        {slide.sections && <EditorialSections sections={slide.sections} isLight={isLight} />}
      </div>
    </div>
  );
};

const PrivacyBoundarySlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const { config } = useS2PElevateDeck();
  const isRockline = config.analyticsVariant === "rockline";
  const screenshotCards = [
    {
      provider: "ChatGPT",
      src: "/screenshots/ai-agent-starter/chatgpt-data-controls.jpg",
      alt: "ChatGPT data controls settings.",
      caption: slide.sections?.find((section) => section.label === "ChatGPT")?.body
        ?? "Review training, history, memory, and connected data before using company work.",
    },
    {
      provider: "Claude",
      src: "/screenshots/masterclass/09-privacy.png",
      alt: "Claude privacy settings.",
      caption: slide.sections?.find((section) => section.label === "Claude")?.body
        ?? "Know what is retained and how it is used before sharing company work.",
    },
  ];
  const orderedScreenshotCards = config.analyticsVariant === "core" ? [...screenshotCards].reverse() : screenshotCards;
  const otherProviders = slide.sections?.filter((section) => section.label === "Microsoft Copilot" || section.label === "Gemini") ?? [];
  const copilotSettings = isRockline ? slide.sections ?? [] : [];
  const hasPrivacyActions = Boolean(slide.bullets?.length || shouldRenderSourceLink(slide));

  return (
    <div data-layout="editorial-privacy-boundary" className="-mx-5 flex min-h-full bg-[#FBF8F4] px-5 py-4 text-[#111827] sm:-mx-8 sm:px-8 sm:py-5 lg:-mx-12 lg:px-12">
      <div className="mx-auto flex min-h-full w-full max-w-[1760px] flex-col justify-center">
        <div className="flex items-start gap-3 sm:gap-4">
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-[#0A1F44] text-white shadow-lg shadow-[#0A1F44]/15 sm:h-12 sm:w-12">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="pt-0.5">
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#E4691E] sm:text-sm lg:text-base">{slide.eyebrow}</p>
            {slide.quote && <p className="mt-1 text-sm font-medium text-slate-500 sm:text-base lg:text-lg">{slide.quote}</p>}
          </div>
        </div>

        <h1 className="mt-3 max-w-[1500px] font-serif text-[clamp(2.85rem,4.95vw,5.65rem)] font-bold leading-[0.96] tracking-[-0.035em] text-[#111827]">
          {slide.title}
        </h1>
        {slide.lead && <p className="mt-4 max-w-6xl text-base font-medium leading-relaxed text-slate-600 sm:text-lg lg:text-xl xl:text-2xl">{slide.lead}</p>}

        <div className={`mt-5 grid gap-6 lg:items-start lg:gap-8 ${isRockline && !hasPrivacyActions ? "lg:grid-cols-1" : "lg:grid-cols-[0.76fr_1.24fr]"}`}>
          {isRockline && !hasPrivacyActions ? null : (
            <div className="lg:pt-14">
              <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-slate-500 sm:text-sm lg:text-base">Do this</p>
              <ol className="mt-3 space-y-3 sm:space-y-4">
                {slide.bullets?.map((bullet, index) => (
                  <li key={bullet} className="grid grid-cols-[34px_1fr] items-start gap-3 sm:grid-cols-[38px_1fr] sm:gap-4">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0A1F44] text-xs font-black text-white sm:h-9 sm:w-9">{index + 1}</span>
                    <span className="pt-1 text-base font-medium leading-relaxed text-slate-700 sm:text-lg lg:text-xl">{bullet}</span>
                  </li>
                ))}
              </ol>
              {shouldRenderSourceLink(slide) && (
                <a href={slide.sourceUrl!} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#0047FF] hover:underline sm:text-base lg:text-lg">
                  {slide.sourceLabel ?? "Open source"} <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          )}

          <div>
            {isRockline ? (
              <section className="overflow-hidden rounded-2xl border border-[#D8DEE9] bg-white shadow-xl shadow-[#0A1F44]/10" aria-label="Microsoft 365 Copilot privacy settings">
                <div className="flex min-h-11 items-center justify-between gap-3 border-b border-[#E5E9F2] bg-[#F5F7FA] px-4 py-2">
                  <span className="flex gap-1.5" aria-hidden="true"><i className="h-2.5 w-2.5 rounded-full bg-[#E46969]" /><i className="h-2.5 w-2.5 rounded-full bg-[#E7BD43]" /><i className="h-2.5 w-2.5 rounded-full bg-[#72C995]" /></span>
                  <strong className="text-[10px] uppercase tracking-[0.2em] text-slate-600 sm:text-xs">Microsoft 365 Copilot · Privacy settings</strong>
                </div>
                <div className={`grid ${copilotSettings.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                  {copilotSettings.map((section, index) => (
                    <article key={section.title} className={`px-4 py-3 sm:px-5 sm:py-4 ${index > 0 ? "border-t border-[#E5E9F2] sm:border-t-0" : ""} ${copilotSettings.length === 3 ? index > 0 ? "sm:border-l sm:border-[#E5E9F2]" : "" : `${index % 2 === 1 ? "sm:border-l sm:border-[#E5E9F2]" : ""} ${index > 1 ? "sm:border-t sm:border-[#E5E9F2]" : ""}`}`}>
                      <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#E4691E] sm:text-xs">{section.label}</p>
                      <h2 className="mt-1 text-lg font-extrabold leading-tight text-[#0A1F44] sm:text-xl">{section.title}</h2>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600 sm:mt-2 sm:text-base">{section.body}</p>
                    </article>
                  ))}
                </div>
              </section>
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                  {orderedScreenshotCards.map((card) => (
                    <figure key={card.provider} className="overflow-hidden rounded-2xl border border-[#D8DEE9] bg-white shadow-xl shadow-[#0A1F44]/10">
                      <div className="flex h-9 items-center justify-between border-b border-[#E5E9F2] bg-[#F5F7FA] px-3">
                        <span className="flex gap-1.5" aria-hidden="true"><i className="h-2.5 w-2.5 rounded-full bg-[#E46969]" /><i className="h-2.5 w-2.5 rounded-full bg-[#E7BD43]" /><i className="h-2.5 w-2.5 rounded-full bg-[#72C995]" /></span>
                        <strong className="text-[10px] uppercase tracking-[0.2em] text-slate-600">{card.provider}</strong>
                      </div>
                      <img src={card.src} alt={card.alt} className="h-[20svh] min-h-40 max-h-[220px] w-full object-cover object-top" />
                      <figcaption className="min-h-[78px] border-t border-[#E5E9F2] px-4 py-3 text-sm leading-relaxed text-slate-600 sm:text-base">{card.caption}</figcaption>
                    </figure>
                  ))}
                </div>

                {otherProviders.length > 0 && (
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {otherProviders.map((section) => (
                      <div key={section.label} className="rounded-xl border border-[#D8DEE9] bg-white/80 px-4 py-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                        <strong className="text-[#0A1F44]">{section.label}:</strong> {section.body}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const LonghandGearOverviewSlide = () => (
  <div data-layout="longhand-gear-overview" className="flex min-h-full w-full items-center justify-center py-2">
    <section
      className="relative mx-auto flex aspect-video w-full max-w-[1180px] flex-col overflow-hidden rounded-2xl border border-[#D8DEE9] bg-[linear-gradient(145deg,#F8FAFC_0%,#FFF_58%,#FDF7F1_100%)] px-11 pb-[34px] pt-[38px] max-[640px]:aspect-auto max-[640px]:px-[17px] max-[640px]:pb-[18px] max-[640px]:pt-[22px]"
      style={{ width: "min(100%, calc((100svh - 152px) * 16 / 9))" }}
      aria-labelledby="longhand-gear-overview-title"
    >
      <span className="absolute inset-x-0 top-0 h-[5px] bg-[linear-gradient(90deg,#F0A46A_0_25%,#EBCB55_25%_50%,#5FC6D8_50%_75%,#77C58A_75%)]" aria-hidden="true" />

      <div className="mb-[10px] max-w-[920px]">
        <h1 id="longhand-gear-overview-title" className="font-serif text-[44px] font-bold leading-[1.08] tracking-[-0.7px] text-[#1F2A44] max-[640px]:text-2xl">
          The 4 Gears of AI Usage
        </h1>
        <p className="mt-[10px] max-w-[820px] text-[17px] font-extrabold leading-[1.4] tracking-[2.3px] text-[#E4691E] max-[640px]:text-sm max-[640px]:leading-[1.45]">
          USE THE SMALLEST GEAR THAT CAN OWN THE RESULT
        </p>
      </div>

      <div className="s2p-gear-spinning relative z-[1] mx-auto -mb-[42px] flex min-h-0 w-full max-w-[1180px] flex-1 items-end justify-center max-[820px]:mb-0 max-[820px]:min-h-[280px] max-[640px]:min-h-0">
        <svg
          className="block h-full w-full overflow-visible max-[640px]:hidden"
          viewBox="60 0 880 330"
          role="img"
          aria-label="Four interlocking AI usage gears resting on a foundation of human intelligence. Chat answers a question. Assistant finishes a deliverable. Agents complete an objective. Loops sustain an outcome."
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <rect id="longhand-gear-tooth" x="-7" y="-58" width="14" height="17" rx="2" />
            <g id="longhand-gear-form">
              <circle r="46" fill="currentColor" fillOpacity=".85" stroke="currentColor" strokeWidth="2.4" />
              <g fill="currentColor" fillOpacity=".95" stroke="currentColor" strokeWidth="1.4">
                {Array.from({ length: 12 }, (_, index) => (
                  <use key={index} href="#longhand-gear-tooth" transform={index === 0 ? undefined : `rotate(${index * 30})`} />
                ))}
              </g>
              <circle r="19" fill="#fff" stroke="currentColor" strokeWidth="2" />
            </g>
          </defs>

          {LONGHAND_GEAR_ITEMS.map((gear) => (
            <g key={gear.number} className="s2p-gear-part" data-gear={gear.number} transform={gear.desktopTransform} style={{ color: gear.color }}>
              <g className={`s2p-gear-rotor s2p-gear-${gear.rotor}`}><g className="s2p-gear-mesh-rotor"><use href="#longhand-gear-form" /></g></g>
              <text className="s2p-gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44" style={{ fontSize: gear.numberSize }}>{gear.number}</text>
            </g>
          ))}

          <g fill="none" strokeWidth="2.5">
            {[...LONGHAND_GEAR_ITEMS].reverse().map((gear) => (
              <path key={gear.number} className="s2p-gear-part" data-gear={gear.number} d={gear.connector} stroke={gear.color} />
            ))}
          </g>

          <g style={{ fontFamily: "inherit" }}>
            {[...LONGHAND_GEAR_ITEMS].reverse().map((gear) => (
              <g key={gear.number} className="s2p-gear-part" data-gear={gear.number} aria-label={`${gear.title}. ${gear.body}.`}>
                <text x="620" y={gear.titleY} fill={gear.accent} style={{ fontSize: 36, fontWeight: 800 }}>{gear.title}</text>
                <text x="620" y={gear.bodyY} fill="#6B7482" style={{ fontSize: 24, fontWeight: 650 }}>{gear.body}</text>
              </g>
            ))}
          </g>
        </svg>

        <div className="hidden w-full max-[640px]:block">
          <svg
            className="mx-auto h-auto w-[min(100%,280px)] overflow-visible"
            viewBox="0 20 500 330"
            role="img"
            aria-label="Four interlocking AI gears resting on a foundation of human intelligence, becoming smaller and faster from Chat through Assistant and Agents to Loops."
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <rect id="longhand-gear-tooth-mobile" x="-7" y="-58" width="14" height="17" rx="2" />
              <g id="longhand-gear-form-mobile">
                <circle r="46" fill="currentColor" fillOpacity=".85" stroke="currentColor" strokeWidth="2.4" />
                <g fill="currentColor" fillOpacity=".95" stroke="currentColor" strokeWidth="1.4">
                  {Array.from({ length: 12 }, (_, index) => (
                    <use key={index} href="#longhand-gear-tooth-mobile" transform={index === 0 ? undefined : `rotate(${index * 30})`} />
                  ))}
                </g>
                <circle r="19" fill="#fff" stroke="currentColor" strokeWidth="2" />
              </g>
            </defs>

            {LONGHAND_GEAR_ITEMS.map((gear) => (
              <g key={gear.number} transform={gear.mobileTransform} style={{ color: gear.color }}>
                <g className={`s2p-gear-rotor s2p-gear-${gear.rotor}`}><use href="#longhand-gear-form-mobile" /></g>
                <text className="s2p-gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44" style={{ fontSize: gear.numberSize }}>{gear.number}</text>
              </g>
            ))}
          </svg>

          <ol className="mt-[9px] grid list-none grid-cols-2 gap-[7px]" aria-label="AI usage gears">
            {LONGHAND_GEAR_ITEMS.map((gear) => (
              <li key={gear.number} className="min-w-0 rounded-[9px] border border-l-[3px] px-2 py-[7px]" style={{ borderColor: gear.color, backgroundColor: gear.tint }}>
                <strong className="block font-serif text-[13px] leading-[1.15]" style={{ color: gear.accent }}>{gear.number} · {gear.title}</strong>
                <span className="mt-0.5 block text-[9.5px] font-semibold leading-[1.3] text-[#6B7482]">{gear.body}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="relative z-[2] grid min-h-[78px] grid-cols-[minmax(240px,.72fr)_minmax(0,1.28fr)] items-center gap-7 rounded-[10px_10px_4px_4px] bg-[linear-gradient(105deg,#1F2A44,#253453)] px-[22px] pb-[14px] pt-[17px] text-white shadow-[0_12px_28px_rgba(31,42,68,.18)] max-[820px]:grid-cols-[minmax(220px,.75fr)_minmax(0,1.25fr)] max-[820px]:gap-5 max-[640px]:mt-[14px] max-[640px]:min-h-0 max-[640px]:grid-cols-1 max-[640px]:gap-2 max-[640px]:px-[14px] max-[640px]:pb-3 max-[640px]:pt-[14px]">
        <span className="absolute inset-x-0 top-0 h-[5px] rounded-t-[10px] bg-[linear-gradient(90deg,#F0A46A_0_25%,#EBCB55_25%_50%,#5FC6D8_50%_75%,#77C58A_75%)]" aria-hidden="true" />
        <span className="absolute -bottom-1.5 -left-1.5 -right-1.5 -z-[1] h-2 rounded-b-[7px] bg-[#141E35] max-[640px]:-bottom-[5px] max-[640px]:-left-[3px] max-[640px]:-right-[3px] max-[640px]:h-1.5" aria-hidden="true" />
        <div className="flex min-w-0 flex-col gap-[3px] max-[640px]:gap-0.5">
          <span className="text-[9.5px] font-black uppercase tracking-[1.6px] text-[#F5B487]">Foundation</span>
          <strong className="font-serif text-2xl leading-[1.1] max-[640px]:text-[19px]">Human Intelligence</strong>
        </div>
        <p className="text-[15px] font-bold leading-[1.45] text-[#DCE2ED] max-[820px]:text-[13.5px] max-[640px]:text-[11.5px] max-[640px]:leading-[1.4]">
          Sets direction · defines good · reviews the work · owns the outcome
        </p>
      </div>
    </section>
  </div>
);

const GearOverviewSlide = () => <LonghandGearOverviewSlide />;

const GearSystemSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  if (slide.id === "four-levels") return <GearOverviewSlide />;

  const isFocused = (number: number) => {
    if (slide.gearFocus === "lower") return number <= 2;
    if (slide.gearFocus === "upper") return number === 0 || number >= 3;
    return true;
  };

  return (
    <div data-layout="editorial-gear-system" className="mx-auto flex min-h-full w-full max-w-[1760px] flex-col justify-center py-6 sm:py-8">
      <h1 className="text-base font-extrabold uppercase tracking-[0.2em] text-[#E4691E] sm:text-lg lg:text-xl">
        MATCH THE GEAR TO THE PROBLEM
      </h1>
      <div className="mt-5 divide-y divide-slate-200/80 border-y border-slate-200/80">
        {GEAR_PROBLEM_ITEMS.map((item) => (
          <div
            key={item.number}
            className={`grid grid-cols-[58px_1fr] items-center gap-4 py-3 transition-opacity sm:grid-cols-[72px_1fr] sm:gap-7 sm:py-4 ${isFocused(item.number) ? "opacity-100" : "opacity-20"}`}
          >
            <span
              className="flex h-12 w-12 items-center justify-center rounded-full border-2 text-xl font-black sm:h-14 sm:w-14 sm:text-2xl"
              style={{ color: item.accent, borderColor: item.accent, backgroundColor: item.tint }}
            >
              {item.number}
            </span>
            <div className="min-w-0 sm:grid sm:grid-cols-[minmax(300px,0.72fr)_1.28fr] sm:items-baseline sm:gap-8">
              <h2 className="s2p-display text-3xl font-semibold uppercase sm:text-4xl" style={{ color: item.accent }}>{item.title}</h2>
              <p className="mt-1 text-lg font-semibold text-[#6B7482] sm:mt-0 sm:text-2xl lg:text-3xl">{item.body}</p>
            </div>
          </div>
        ))}
      </div>
      {shouldRenderSourceLink(slide) && (
        <a href={slide.sourceUrl!} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex w-fit items-center gap-2 text-base font-bold text-[#0047FF] sm:text-lg">
          {slide.sourceLabel ?? "Open source"} <ExternalLink className="h-4 w-4" />
        </a>
      )}
    </div>
  );
};

const promptSegmentTone = (title: string) => {
  if (title === "VERB") return "orange";
  if (title === "OUTCOME") return "blue";
  if (title === "CONSTRAINTS") return "green";
  if (title === "SOURCES") return "yellow";
  return "neutral";
};

const PromptExplorerSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number | null>(null);
  const segments = slide.sections ?? [];
  const activeSegment = activeSegmentIndex === null ? null : segments[activeSegmentIndex];

  return (
    <div
      data-layout="editorial-prompt-explorer"
      className="relative -mx-5 flex min-h-[calc(100svh-140px)] w-[calc(100%+2.5rem)] flex-col justify-center overflow-hidden bg-[#020817] px-5 py-6 sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
      onMouseLeave={(event) => {
        if (!event.currentTarget.matches(":focus-within")) setActiveSegmentIndex(null);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActiveSegmentIndex(null);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          setActiveSegmentIndex(null);
        }
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.028) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.028) 1px, transparent 1px), radial-gradient(circle at 82% 20%, rgba(56,189,248,.13), transparent 30%), radial-gradient(circle at 8% 92%, rgba(255,116,32,.14), transparent 30%)",
          backgroundSize: "64px 64px, 64px 64px, auto, auto",
        }}
      />
      <div className="relative mx-auto flex w-full max-w-[1760px] flex-col justify-center">
        <p className="flex items-center gap-4 text-xs font-extrabold uppercase tracking-[0.24em] text-[#38BDF8] sm:text-sm">
          <span className="h-1 w-12 bg-[#38BDF8] sm:w-16" aria-hidden="true" />
          {slide.eyebrow}
        </p>

        <div
          className="mt-7 flex w-full flex-wrap content-center gap-x-[0.26em] gap-y-[0.18em] font-serif text-[clamp(2.5rem,5.75vw,7.2rem)] font-semibold leading-[1.04] tracking-[-0.05em] text-white sm:mt-9"
          role="group"
          aria-label="How might we verb outcome without constraints based on sources"
        >
          {segments.map((segment, index) => {
            const tone = promptSegmentTone(segment.title);
            const displayTitle = index === 0
              ? `“${segment.title}`
              : index === segments.length - 1
                ? `${segment.title}”`
                : segment.title;
            return (
              <button
                key={`${segment.title}-${index}`}
                type="button"
                className="s2p-prompt-segment"
                data-tone={tone}
                aria-controls="s2p-prompt-detail"
                aria-expanded={activeSegmentIndex === index}
                onMouseEnter={() => setActiveSegmentIndex(index)}
                onFocus={() => setActiveSegmentIndex(index)}
                onClick={() => setActiveSegmentIndex(index)}
              >
                {displayTitle}
              </button>
            );
          })}
        </div>

        <div className={`relative mt-6 border-t border-white/15 transition-[min-height] duration-300 sm:min-h-[128px] ${activeSegment ? "min-h-[180px]" : "min-h-[116px]"}`}>
          <div aria-hidden={Boolean(activeSegment)} className={`absolute inset-0 flex items-center gap-3 text-base font-semibold tracking-[0.02em] text-[#91A0B6] transition-all duration-200 sm:text-lg lg:text-xl ${activeSegment ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100"}`}>
            <span className="text-2xl text-[#FF7420]" aria-hidden="true">↖</span>
            Hover, tap, or focus any segment to see what it does.
          </div>
          <div
            id="s2p-prompt-detail"
            className={`s2p-prompt-detail absolute inset-x-0 top-3 grid min-h-[96px] grid-cols-1 items-center gap-2 overflow-hidden rounded-md px-4 py-3 transition-all duration-300 sm:grid-cols-[minmax(150px,.35fr)_1fr] sm:gap-6 sm:px-6 ${activeSegment ? "translate-y-0 scale-100 opacity-100" : "pointer-events-none -translate-y-2 scale-[.985] opacity-0"}`}
            data-tone={activeSegment ? promptSegmentTone(activeSegment.title) : "neutral"}
            aria-live="polite"
            aria-atomic="true"
            aria-hidden={!activeSegment}
          >
            {activeSegment && (
              <>
                <div>
                  <p className="s2p-prompt-detail-label text-xs font-extrabold uppercase tracking-[0.14em] sm:text-sm">{activeSegment.label}</p>
                  <h2 className="mt-1 font-serif text-3xl font-semibold leading-none tracking-[-0.035em] text-white sm:text-4xl">{activeSegment.title}</h2>
                </div>
                <p className="text-base leading-relaxed text-[#D1DBE8] sm:text-lg lg:text-xl">{activeSegment.body}</p>
              </>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs font-bold uppercase tracking-[0.11em] sm:text-sm">
          <span className="s2p-prompt-key text-[#FF7420]">Action</span>
          <span className="s2p-prompt-key text-[#38BDF8]">Definition of done</span>
          <span className="s2p-prompt-key text-[#34D399]">Rules + boundaries</span>
          <span className="s2p-prompt-key text-[#FBBF24]">Sources</span>
        </div>
      </div>
    </div>
  );
};

const OutcomeFirstPromptExampleSlide = ({ slide }: { slide: S2PElevateSlide }) => (
  <div
    data-layout="ten-x-prompt-example"
    className="relative -mx-5 flex min-h-[calc(100svh-140px)] w-[calc(100%+2.5rem)] items-center overflow-hidden bg-[#142445] px-5 py-7 text-white sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 sm:py-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
  >
    <div
      className="pointer-events-none absolute inset-0 opacity-60"
      aria-hidden="true"
      style={{
        backgroundImage:
          "radial-gradient(circle at 88% 12%, rgba(95,198,216,.10), transparent 32%), radial-gradient(circle at 8% 92%, rgba(0,71,255,.14), transparent 34%)",
      }}
    />
    <div className="relative mx-auto flex w-full max-w-[1760px] flex-col justify-center">
      <div className="flex items-center justify-between gap-5">
        <p className="flex items-center gap-4 text-xs font-extrabold uppercase tracking-[0.24em] text-[#5FC6D8] sm:text-sm lg:text-base">
          <span className="h-1 w-12 bg-[#5FC6D8] sm:w-16" aria-hidden="true" />
          {slide.eyebrow}
        </p>
        {shouldRenderSourceLink(slide) && (
          <a
            href={slide.sourceUrl!}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 text-sm font-bold text-white/65 transition-colors hover:text-white focus-visible:text-white sm:inline-flex lg:text-base"
          >
            {slide.sourceLabel ?? "Open source"} <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
      <div className="relative mt-7 border-l-[7px] border-[#FF7420] bg-[#FAF8F5] px-6 py-7 text-[#171F2F] shadow-2xl shadow-black/15 sm:px-9 sm:py-9 lg:mt-9 lg:px-14 lg:py-12">
        <span className="absolute right-5 top-4 text-[10px] font-extrabold uppercase tracking-[0.28em] text-[#8A94A3] sm:right-8 sm:top-6 sm:text-xs">Example</span>
        <blockquote className="s2p-editorial max-w-[29ch] pt-4 text-[clamp(1.65rem,3.5vw,4.4rem)] font-semibold leading-[1.08] tracking-[-0.04em] sm:pt-3">
          {slide.title}
        </blockquote>
      </div>
    </div>
  </div>
);

const GoodExamplesSlide = ({ slide }: { slide: S2PElevateSlide }) => (
  <div
    data-layout="ten-x-good-examples"
    className="relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] flex-col justify-center overflow-hidden bg-[#020817] px-5 py-5 text-white sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
  >
    <div
      className="pointer-events-none absolute inset-0 opacity-70"
      aria-hidden="true"
      style={{
        backgroundImage: "linear-gradient(rgba(95,198,216,.055) 1px, transparent 1px), linear-gradient(90deg, rgba(95,198,216,.055) 1px, transparent 1px), radial-gradient(circle at 88% 14%, rgba(0,71,255,.2), transparent 34%), radial-gradient(circle at 8% 92%, rgba(228,105,30,.16), transparent 32%)",
        backgroundSize: "72px 72px, 72px 72px, auto, auto",
      }}
    />
    <div className="relative mx-auto grid w-full max-w-[1760px] gap-6 lg:grid-cols-[.82fr_1.18fr] lg:items-center lg:gap-12">
      <div>
        <EditorialHeader slide={slide} isLight={false} compact />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#FF7420] sm:text-sm">Six checks before scheduling</p>
        <ol className="mt-3 grid border-y border-white/20 sm:grid-cols-2">
          {slide.bullets?.map((bullet, index) => (
            <li key={bullet} className={`grid grid-cols-[42px_1fr] items-start gap-3 border-b border-white/20 py-3 sm:px-4 ${index % 2 === 1 ? "sm:border-l" : ""} ${index >= (slide.bullets?.length ?? 0) - 2 ? "sm:border-b-0" : ""}`}>
              <span className="s2p-editorial text-2xl font-bold leading-none text-[#5FC6D8] sm:text-3xl">0{index + 1}</span>
              <span className="text-base font-semibold leading-snug text-white sm:text-lg lg:text-xl">{bullet}</span>
            </li>
          ))}
        </ol>
        {slide.note && (
          <p className="mt-4 border-l-4 border-[#FF7420] pl-4 text-sm font-medium leading-relaxed text-slate-300 sm:text-base lg:text-lg">{slide.note}</p>
        )}
        {slide.practice && <PracticePanel practice={slide.practice} slideId={slide.id} />}
      </div>
    </div>
  </div>
);

const DecisionRightsSlide = ({ slide }: { slide: S2PElevateSlide }) => (
  <div data-layout="editorial-decision-rights" className="mx-auto grid min-h-full w-full max-w-[1760px] gap-7 py-5 xl:grid-cols-[.78fr_1.22fr] xl:items-center xl:gap-12 xl:py-7">
    <div>
      <EditorialHeader slide={slide} isLight={false} compact />
      <p className="s2p-editorial mt-5 border-l-4 border-[#5FC6D8] pl-5 text-2xl font-semibold leading-snug text-white sm:text-3xl lg:text-4xl">Read → Draft → Change → Publish</p>
      <div className="mt-5 flex flex-col gap-3"><EditorialCallouts slide={slide} isLight={false} /><EditorialSourceLink slide={slide} isLight={false} /></div>
    </div>
    <div className="min-w-0 space-y-4">
      {slide.sections && (
        <div className="border-y border-white/15">
          {slide.sections.map((section, index) => (
            <article key={section.label} className="grid items-baseline gap-x-4 gap-y-2 border-b border-white/15 py-3.5 last:border-b-0 md:grid-cols-[minmax(220px,.82fr)_minmax(170px,.65fr)_minmax(0,1.53fr)]">
              <span className="s2p-editorial text-3xl font-bold leading-none 2xl:text-4xl" style={{ color: editorialAccent(index, false) }}>{section.label}</span>
              <h2 className="s2p-display text-lg font-semibold uppercase leading-tight text-white sm:text-xl lg:text-2xl">{section.title}</h2>
              <p className="text-sm leading-relaxed text-slate-300 sm:text-base lg:text-lg">{section.body}</p>
            </article>
          ))}
        </div>
      )}
      {slide.prompt && <PromptPanel label={slide.promptLabel ?? "Team rule"} value={slide.prompt} slideId={slide.id} />}
      {slide.bullets && (
        <div className="grid border-y border-white/15 sm:grid-cols-2">
          {slide.bullets.map((bullet, index) => (
            <p key={bullet} className={`grid grid-cols-[42px_1fr] gap-3 py-3 text-sm leading-relaxed text-slate-300 sm:text-base lg:text-lg ${index > 0 ? "sm:border-l sm:border-white/15 sm:pl-5" : "sm:pr-5"}`}>
              <span className="s2p-editorial text-2xl font-bold leading-none sm:text-3xl" style={{ color: editorialAccent(index, false) }}>0{index + 1}</span>{bullet}
            </p>
          ))}
        </div>
      )}
    </div>
  </div>
);

const EaseImpactSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const cells = slide.sections ?? [];
  const cellStyles = [
    "border-emerald-300/40 bg-emerald-300/15",
    "border-sky-300/40 bg-sky-300/15",
    "border-cyan-200/30 bg-cyan-200/10",
    "border-orange-300/40 bg-orange-300/10",
  ];

  return (
    <div data-layout="editorial-ease-impact" className="mx-auto flex min-h-full w-full max-w-[1760px] flex-col justify-center py-5 sm:py-7">
      <EditorialEyebrow isLight={false}>{slide.eyebrow}</EditorialEyebrow>
      <h1 className="s2p-editorial mt-3 text-[clamp(2.85rem,4.9vw,5.75rem)] font-bold leading-[.96] tracking-[-0.045em] text-white">{slide.title}</h1>
      <p className="mt-3 max-w-6xl text-base leading-relaxed text-slate-300 sm:text-lg lg:text-xl">{slide.lead}</p>

      <div className="mt-5 grid gap-6 lg:grid-cols-[1.28fr_0.72fr] lg:items-center">
        <div className="grid grid-cols-[30px_1fr] grid-rows-[1fr_auto] gap-x-3 gap-y-2">
          <div className="flex items-center justify-center">
            <span className="-rotate-90 whitespace-nowrap text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#9DB6FF]">Impact · low → high</span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            {cells.map((section, index) => (
              <article key={section.label} className={`min-h-[126px] border p-3 sm:min-h-[142px] sm:p-5 ${cellStyles[index]}`}>
                <div className="flex items-start justify-between gap-3">
                  <span className="s2p-editorial text-5xl font-black leading-none sm:text-6xl" style={{ color: editorialAccent(index, false) }}>{section.label}</span>
                  <span className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#9DB6FF]">{index < 2 ? "High impact" : "Lower impact"}</span>
                </div>
                <h2 className="s2p-display mt-1 text-xl font-semibold uppercase text-white sm:text-2xl lg:text-3xl">{section.title}</h2>
                <p className="mt-1 text-sm leading-relaxed text-slate-300 sm:text-base lg:text-lg">{section.body}</p>
              </article>
            ))}
          </div>
          <div />
          <p className="text-center text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#9DB6FF]">Degree of difficulty · low → high</p>
        </div>

        <div className="space-y-3">
          {[
            ["01", "Explore", "Browse the library for repeated tasks, decisions, handoffs, or bottlenecks you already own."],
            ["02", "Plot", "Place each idea in A, B, C, or X based on impact and degree of difficulty."],
            ["03", "Choose", "Circle 1–2 A ideas to build now. Save one B and one or two C ideas for Session 3."],
          ].map(([number, title, body]) => (
            <div key={number} className="grid grid-cols-[46px_1fr] gap-3 border-t border-white/15 py-3 first:border-t-0">
              <span className="s2p-editorial text-3xl font-bold leading-none text-[#5FC6D8]">{number}</span>
              <div><p className="text-lg font-bold text-white sm:text-xl">{title}</p><p className="mt-1 text-sm leading-relaxed text-slate-300 sm:text-base lg:text-lg">{body}</p></div>
            </div>
          ))}
          {slide.note && <p className="border-l-4 border-[#5FC6D8] bg-white/[0.06] p-4 text-base font-semibold leading-relaxed text-white sm:text-lg">{slide.note}</p>}
          {shouldRenderSourceLink(slide) && <a href={slide.sourceUrl!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-base font-bold text-[#9DB6FF] sm:text-lg">{slide.sourceLabel ?? "Open source"} <ExternalLink className="h-4 w-4" /></a>}
        </div>
      </div>
    </div>
  );
};

const ShowcaseCapability = ({ item, slide }: { item: S2PElevateShowcase; slide: S2PElevateSlide }) => {
  const { config } = useS2PElevateDeck();
  const isLight = slide.tone === "paper" || slide.tone === "cloud";
  const longTitle = item.title.length > 48;
  return (
    <div data-layout="editorial-showcase-capability" className="mx-auto grid min-h-full w-full max-w-[1760px] gap-8 py-4 lg:grid-cols-[.86fr_1.14fr] lg:items-center lg:gap-12 lg:py-5">
      <div>
        <EditorialEyebrow isLight={isLight}>{slide.eyebrow}</EditorialEyebrow>
        <p className={`mt-4 text-xs font-extrabold uppercase tracking-[0.18em] sm:text-sm ${isLight ? "text-slate-500" : "text-slate-400"}`}>{item.category}</p>
        <h1 className={`s2p-editorial mt-2 ${longTitle ? "text-[clamp(2.75rem,4.9vw,5.6rem)]" : "text-[clamp(3rem,5.35vw,6.2rem)]"} font-bold leading-[.94] tracking-[-0.05em] ${isLight ? "text-[#111827]" : "text-white"}`}>{item.title}</h1>
        <p className={`mt-4 max-w-4xl text-base font-semibold leading-relaxed sm:text-lg lg:text-xl xl:text-2xl ${isLight ? "text-slate-700" : "text-blue-100"}`}>{item.lead}</p>
        <div className={`mt-5 border-y ${isLight ? "border-slate-200" : "border-white/15"}`}>
          {item.bullets.map((bullet, index) => (
            <div key={bullet} className={`grid grid-cols-[42px_1fr] gap-3 border-b py-3 last:border-b-0 ${isLight ? "border-slate-200" : "border-white/15"}`}>
              <span className="s2p-editorial text-3xl font-bold leading-none" style={{ color: editorialAccent(index, isLight) }}>0{index + 1}</span>
              <span className={`text-base font-semibold leading-relaxed sm:text-lg lg:text-xl ${isLight ? "text-slate-700" : "text-slate-200"}`}>{bullet}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        {item.image ? (
          <figure className="overflow-hidden border border-[#D6DEEC] bg-white shadow-2xl shadow-[#0A1F44]/15">
            <div className="flex h-10 items-center justify-between border-b border-[#E5E9F2] bg-[#F5F7FA] px-4">
              <span className="flex gap-1.5" aria-hidden="true"><i className="h-2.5 w-2.5 rounded-full bg-[#E46969]" /><i className="h-2.5 w-2.5 rounded-full bg-[#E7BD43]" /><i className="h-2.5 w-2.5 rounded-full bg-[#72C995]" /></span>
              <strong className="text-[10px] uppercase tracking-[0.2em] text-slate-600">{item.platform}</strong>
            </div>
            <img src={item.image} alt={item.imageAlt ?? ""} className="h-[36svh] min-h-64 max-h-[420px] w-full object-cover object-center" />
          </figure>
        ) : (
          <div className={`border-y py-2 ${isLight ? "border-slate-200 text-[#0A1F44]" : "border-white/15 text-white"}`}>
            <p className={`text-[10px] font-extrabold uppercase tracking-[0.2em] sm:text-xs ${isLight ? "text-[#0047FF]" : "text-[#5FC6D8]"}`}>The agent pattern</p>
            <div className="mt-3">
              {["Read the authorized context", "Complete the multi-step work", "Pause at the human decision"].map((step, index) => (
                <div key={step} className={`grid grid-cols-[88px_1fr] items-center gap-4 border-b py-4 last:border-b-0 ${isLight ? "border-slate-200" : "border-white/15"}`}>
                  <span className="s2p-editorial text-6xl font-bold leading-none opacity-80" style={{ color: editorialAccent(index, isLight) }}>0{index + 1}</span>
                  <span className="s2p-editorial text-xl font-semibold leading-tight sm:text-2xl">{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className={`mt-4 grid gap-3 border-l-4 p-4 sm:grid-cols-[auto_1fr] sm:items-baseline ${isLight ? "border-[#0047FF] bg-[#E7EEFF]/65 text-[#0A1F44]" : "border-[#5FC6D8] bg-white/[0.06] text-slate-200"}`}>
          <strong className="text-xs uppercase tracking-[0.15em] sm:text-sm">What makes this agentic</strong>
          <span className="text-sm leading-relaxed sm:text-base lg:text-lg">{item.agentic}</span>
        </div>
        {item.sourceUrl && (
          <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className={`mt-3 inline-flex items-center gap-2 text-sm font-bold sm:text-base lg:text-lg ${isLight ? "text-[#0047FF]" : "text-[#9DB6FF]"}`}>
            {config.showcaseSourceLabel ?? "Vendor source"} <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
};

const ShowcaseApplications = ({ item, slide }: { item: S2PElevateShowcase; slide: S2PElevateSlide }) => {
  const { config } = useS2PElevateDeck();
  return (
    <div data-layout="editorial-showcase-applications" className="mx-auto flex min-h-full w-full max-w-[1760px] flex-col justify-center py-4 sm:py-5">
    <EditorialEyebrow isLight={false}>{slide.eyebrow}</EditorialEyebrow>
    <div className="mt-3 grid gap-4 lg:grid-cols-[.8fr_1.2fr] lg:items-end lg:gap-8">
      <h1 className={`s2p-editorial ${item.title.length > 48 ? "text-[clamp(2.6rem,4.55vw,5.2rem)]" : "text-[clamp(2.9rem,5vw,5.8rem)]"} font-bold leading-[.94] tracking-[-0.05em] text-white`}>{item.title}</h1>
      <p className="border-l-4 border-[#5FC6D8] pl-5 text-base font-medium leading-relaxed text-blue-100 sm:text-lg lg:text-xl xl:text-2xl">{item.implication}</p>
    </div>
    <div className="mt-5 grid gap-7 lg:grid-cols-[1.05fr_0.95fr]">
      <div>
        <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#5FC6D8] sm:text-sm">Apply across the business</p>
        <div className="border-y border-white/15">
          {item.applications.map((application, index) => (
            <div key={`${application.function}-${application.use}`} className="grid grid-cols-[48px_108px_1fr] gap-3 border-b border-white/15 py-3 text-sm last:border-b-0 sm:grid-cols-[54px_130px_1fr] sm:text-base lg:text-lg">
              <span className="s2p-editorial text-3xl font-bold leading-none" style={{ color: editorialAccent(index, false) }}>0{index + 1}</span>
              <span className="font-extrabold text-white">{application.function}</span>
              <span className="text-slate-300">{application.use}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#5FC6D8] sm:text-sm">{config.showcaseSupportLabel ?? "How the four vendors compare"}</p>
        <div className="border-y border-white/15">
          {item.support.map((row) => (
            <div key={row.provider} className="grid gap-1 border-b border-white/15 py-3 last:border-b-0 sm:grid-cols-[150px_1fr] sm:gap-4">
              <div className="flex items-center gap-2 sm:block">
                <span className="font-extrabold text-white">{row.provider}</span>
                <span className={`inline-flex border px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.08em] sm:mt-1 sm:w-fit ${STATUS_CLASSES[row.tone]}`}>{row.status}</span>
              </div>
              <p className="text-sm leading-relaxed text-slate-300 sm:text-base lg:text-lg">{row.detail}</p>
            </div>
          ))}
        </div>
        {item.sourceUrl && (
          <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#9DB6FF] sm:text-base">
            {config.showcaseSourceLabel ?? "Check the latest vendor details"} <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  </div>
  );
};

const UseCaseLibrarySlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const { program } = useS2PElevateDeck();
  const isLight = slide.tone === "paper" || slide.tone === "cloud";
  const items = (slide.showcaseIds ?? [])
    .map((id) => program.showcase.find((item) => item.id === id))
    .filter((item): item is S2PElevateShowcase => Boolean(item));

  return (
    <div data-layout="editorial-use-case-library" className="mx-auto flex min-h-full w-full max-w-[1760px] flex-col justify-center py-4 sm:py-5">
      <EditorialEyebrow isLight={isLight}>{slide.eyebrow}</EditorialEyebrow>
      <div className="mt-3 grid gap-3 lg:grid-cols-[1.08fr_.92fr] lg:items-end lg:gap-8">
        <h1 className={`s2p-editorial text-[clamp(2.7rem,4.6vw,5.2rem)] font-bold leading-[.94] tracking-[-0.05em] ${isLight ? "text-[#111827]" : "text-white"}`}>{slide.title}</h1>
        {slide.lead && <p className={`border-l-4 pl-5 text-base font-medium leading-relaxed sm:text-lg lg:text-xl ${isLight ? "border-[#0047FF] text-slate-700" : "border-[#5FC6D8] text-blue-100"}`}>{slide.lead}</p>}
      </div>
      <div className={`mt-5 grid border-y sm:grid-cols-2 ${isLight ? "border-slate-300" : "border-white/20"}`}>
        {items.map((item, index) => (
          <article key={item.id} className={`grid grid-cols-[52px_1fr] gap-3 border-b p-3 sm:min-h-36 sm:gap-4 sm:p-4 sm:[&:nth-last-child(-n+2)]:border-b-0 sm:[&:nth-child(odd)]:border-r lg:grid-cols-[60px_1fr] ${isLight ? "border-slate-300" : "border-white/20"}`}>
            <span className="s2p-editorial text-4xl font-bold leading-none sm:text-5xl" style={{ color: editorialAccent(index, isLight) }}>0{index + 1}</span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className={`text-[9px] font-extrabold uppercase tracking-[0.14em] sm:text-[10px] ${isLight ? "text-slate-500" : "text-slate-400"}`}>{item.category}</p>
                <span className={`w-fit border px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.12em] sm:text-[10px] ${isLight ? "border-slate-300 text-[#0047FF]" : "border-white/25 text-[#9DB6FF]"}`}>{item.platform}</span>
              </div>
              <h2 className={`s2p-editorial mt-1 text-lg font-semibold leading-tight sm:text-xl lg:text-[1.4rem] ${isLight ? "text-[#0A1F44]" : "text-white"}`}>{item.title}</h2>
              <p className={`mt-1 text-xs leading-relaxed sm:text-sm ${isLight ? "text-slate-600" : "text-slate-300"}`}>{item.lead}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

const CoverEditorialSlide = ({ slide, onStart }: { slide: S2PElevateSlide; onStart: () => void }) => {
  const { program, config } = useS2PElevateDeck();
  const isTechLeaders = config.brand === "tech-leaders";
  const isRockline = config.analyticsVariant === "rockline";
  const isCore = config.analyticsVariant === "core";
  return (
    <div data-layout="editorial-cover" className={`relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] flex-col justify-center overflow-hidden px-5 py-5 text-white sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12 ${isTechLeaders ? "bg-[#080808]" : "bg-[#0047FF]"}`}>
    <div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{ backgroundImage: isTechLeaders ? "linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px), radial-gradient(circle at 88% 20%, rgba(31,42,68,.72), transparent 34%), radial-gradient(circle at 7% 90%, rgba(255,116,32,.28), transparent 30%)" : "linear-gradient(rgba(255,255,255,.055) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.055) 1px, transparent 1px), radial-gradient(circle at 88% 20%, rgba(95,198,216,.5), transparent 30%), radial-gradient(circle at 7% 90%, rgba(228,105,30,.4), transparent 30%)", backgroundSize: "72px 72px, 72px 72px, auto, auto" }} />
    <div className="relative mx-auto w-full max-w-[1760px]">
      <div className="flex items-center justify-between gap-5">
        <p className={`flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.23em] sm:text-sm lg:text-base ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#C9D7FF]"}`}><span className={`h-0.5 w-12 ${isTechLeaders ? "bg-[#FF7420]" : "bg-[#5FC6D8]"}`} />{config.coverEyebrow ?? slide.eyebrow}</p>
        {isRockline || config.showCoverLogo === false ? null : <Logo compact darkSurface />}
      </div>
      <div className={`mt-6 ${isCore ? "" : "grid gap-6 lg:grid-cols-[1.2fr_.8fr] lg:items-end"}`}>
        <div>
          <h1 className="s2p-editorial max-w-5xl text-[clamp(3.4rem,7.4vw,8.4rem)] font-bold leading-[0.82] tracking-[-0.065em]">
            {config.coverTitle ?? <>AI Tools <span className={isTechLeaders ? "text-[#FF7420]" : "text-[#5FC6D8]"}>&amp;</span><br />Workflows</>}
          </h1>
          {config.coverAudience !== null && (
            <p className={`s2p-editorial mt-3 text-[clamp(1.8rem,3.6vw,4.2rem)] font-semibold leading-none tracking-[-0.045em] ${isTechLeaders ? "text-[#FCEBDE]" : "text-[#C9D7FF]"}`}>{config.coverAudience ?? "for Leaders"}</p>
          )}
        </div>
        {isCore ? null : (
          <div className="border-l-4 border-[#E4691E] pl-5">
            <p className="max-w-2xl text-base font-medium leading-relaxed text-blue-50 sm:text-xl lg:text-2xl">{slide.lead}</p>
            {isRockline ? null : (
              <button type="button" onClick={onStart} className={`mt-5 inline-flex min-h-12 items-center gap-3 rounded-full bg-white px-6 text-sm font-bold shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:text-base ${isTechLeaders ? "text-[#1F2A44] shadow-black/30 hover:bg-[#FCEBDE]" : "text-[#0047FF] shadow-[#002A9B]/30 hover:bg-[#E7EEFF]"}`}>
                Start the workshop <ArrowRight className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
      </div>
      <ol className="mt-7 grid border-y border-white/20 sm:grid-cols-5">
        {program.sessions.map((session, index) => (
          <li key={session.id} className="grid grid-cols-[42px_1fr] items-center gap-2 border-b border-white/20 py-2.5 last:border-b-0 sm:block sm:border-b-0 sm:border-l sm:px-3 sm:py-4 sm:first:border-l-0">
            <span className={`s2p-editorial text-3xl font-bold leading-none sm:text-4xl ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#5FC6D8]"}`}>0{session.id}</span>
            <span className="mt-1 block text-sm font-bold leading-tight text-white sm:text-base lg:text-lg">{config.coverSessionTitles?.[index] ?? session.label}</span>
          </li>
        ))}
      </ol>
    </div>
  </div>
  );
};

const SessionEditorialSlide = ({ slide }: { slide: S2PElevateSlide }) => {
  const { program, config } = useS2PElevateDeck();
  const session = program.sessions.find((item) => item.id === slide.session)!;
  const isSimplified = slide.id === "session-3"
    || slide.id === "session-4"
    || slide.id === "session-5"
    || (config.analyticsVariant === "rockline" && slide.id === "session-1");
  const isTechLeaders = config.brand === "tech-leaders";
  return (
    <div data-layout="editorial-session" className={`relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] flex-col justify-center overflow-hidden px-5 py-5 text-white sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12 ${isTechLeaders ? "bg-[#080808]" : "bg-[#0047FF]"}`}>
      <span className="s2p-editorial pointer-events-none absolute -bottom-16 right-3 text-[16rem] font-bold leading-none text-white/[0.055] sm:text-[25rem] lg:right-10" aria-hidden="true">0{session.id}</span>
      <div className={`relative mx-auto grid w-full max-w-[1760px] gap-8 ${isSimplified && !slide.opening ? "lg:grid-cols-1" : "lg:grid-cols-[.8fr_1.2fr] lg:items-center lg:gap-14"}`}>
        <div>
          <p className={`flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.23em] sm:text-sm lg:text-base ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#5FC6D8]"}`}><span className={`h-0.5 w-12 ${isTechLeaders ? "bg-[#FF7420]" : "bg-[#5FC6D8]"}`} />{slide.eyebrow}</p>
          <p className={`s2p-editorial mt-4 font-bold leading-[.72] tracking-[-0.07em] text-white ${slide.opening ? "text-7xl" : "text-[clamp(4.5rem,9vw,10rem)]"}`}>0{session.id}</p>
          <h1 className={`s2p-editorial mt-4 font-bold leading-[.93] tracking-[-0.05em] ${slide.opening ? "text-[clamp(2.8rem,4.5vw,5rem)]" : "text-[clamp(3rem,5.25vw,6.1rem)]"}`}>{slide.title}</h1>
          {slide.opening && <p data-session-focus className="mt-4 max-w-xl text-base font-semibold leading-relaxed text-blue-100 sm:text-xl">{slide.opening.focus}</p>}
          {!isSimplified && slide.lead && <p className="mt-4 max-w-2xl text-base font-medium leading-relaxed text-blue-100 sm:text-xl lg:text-2xl">{slide.lead}</p>}
        </div>
        {slide.opening && <div data-session-opening className="border-l-4 border-[#5FC6D8] pl-5">
          <h2 className="text-sm font-extrabold uppercase tracking-wide text-[#B7CAFF]">Start with progress · 2 minutes</h2>
          <p className="s2p-editorial mt-3 text-2xl font-semibold leading-snug sm:text-3xl">{slide.opening.progress}</p>
          <p className="mt-3 text-sm leading-relaxed text-blue-100">{slide.opening.participation}</p>
        </div>}
        {!isSimplified && (
          <div data-section="session-outcome">
            <p className={`text-xs font-extrabold uppercase tracking-[0.2em] sm:text-sm lg:text-base ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#5FC6D8]"}`}>{config.sessionNoun ?? "Session"} outcome</p>
            <p className="s2p-editorial mt-3 max-w-4xl text-3xl font-semibold leading-snug sm:text-4xl lg:text-5xl">{session.outcome}</p>
            {slide.bullets && (
              <ol className="mt-6 border-y border-white/20">
                {slide.bullets.map((bullet, index) => (
                  <li key={bullet} className="grid grid-cols-[48px_1fr] items-center gap-3 border-b border-white/20 py-3 last:border-b-0">
                    <span className={`s2p-editorial text-3xl font-bold leading-none ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#5FC6D8]"}`}>0{index + 1}</span>
                    <span className="text-base font-semibold leading-relaxed sm:text-lg lg:text-xl">{bullet}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const FinishEditorialSlide = ({ slide, onGoToSlide }: { slide: S2PElevateSlide; onGoToSlide: (id: string) => void }) => {
  const { program, config } = useS2PElevateDeck();
  const isTechLeaders = config.brand === "tech-leaders";
  const isRockline = config.analyticsVariant === "rockline";
  return (
    <div data-layout="editorial-finish" className={`relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] flex-col justify-center overflow-hidden px-5 py-5 text-white sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12 ${isTechLeaders ? "bg-[#080808]" : "bg-[#0047FF]"}`}>
    <div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{ backgroundImage: isTechLeaders ? "radial-gradient(circle at 85% 15%, rgba(31,42,68,.7), transparent 32%), radial-gradient(circle at 8% 90%, rgba(255,116,32,.3), transparent 30%)" : "radial-gradient(circle at 85% 15%, rgba(95,198,216,.42), transparent 30%), radial-gradient(circle at 8% 90%, rgba(228,105,30,.42), transparent 30%)" }} />
    <div className={`relative mx-auto grid w-full max-w-[1760px] lg:grid-cols-[.9fr_1.1fr] lg:items-center ${isRockline ? "gap-6 lg:gap-9" : "gap-8 lg:gap-14"}`}>
      <div>
        <p className={`flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.23em] sm:text-sm lg:text-base ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#5FC6D8]"}`}><span className={`h-0.5 w-12 ${isTechLeaders ? "bg-[#FF7420]" : "bg-[#5FC6D8]"}`} />{config.finishEyebrow ?? slide.eyebrow}</p>
        <h1 className={`s2p-editorial mt-4 font-bold leading-[.9] tracking-[-0.06em] ${isRockline ? "text-[clamp(2.65rem,5vw,5.7rem)]" : "text-[clamp(3rem,6.2vw,7rem)]"}`}>{slide.title}</h1>
        <p className="mt-4 max-w-3xl text-base font-medium leading-relaxed text-blue-100 sm:text-xl lg:text-2xl">{slide.lead}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button type="button" onClick={() => onGoToSlide(program.slides[0].id)} className="inline-flex min-h-12 items-center gap-2 rounded-full border border-white/35 px-5 text-sm font-bold hover:bg-white/10"><RotateCcw className="h-4 w-4" /> Review the workshop</button>
          {config.finishCtaHref && <a href={config.finishCtaHref} onClick={(event) => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            const href = config.finishCtaHref ?? "";
            const prefix = config.slideBasePath ? `${config.slideBasePath}#` : "#";
            const targetId = href.startsWith("#") ? href.slice(1) : href.startsWith(prefix) ? href.slice(prefix.length) : null;
            if (!targetId || !program.slides.some((item) => item.id === targetId)) return;
            event.preventDefault();
            onGoToSlide(targetId);
          }} className={`inline-flex min-h-12 items-center gap-2 rounded-full px-5 text-sm font-bold ${isTechLeaders ? "bg-[#FF7420] text-white hover:bg-[#E4691E]" : "bg-[#5FC6D8] text-[#07172F] hover:bg-[#8DDBE8]"}`}>{config.finishCtaLabel ?? "Open the optional bonus"} <ArrowRight className="h-4 w-4" /></a>}
        </div>
      </div>
      <div>
        <p className={`text-xs font-extrabold uppercase tracking-[0.2em] sm:text-sm lg:text-base ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#5FC6D8]"}`}>{config.finishBulletsLabel ?? "The evolving artifact"}</p>
        {slide.bullets && (
          <ol className="mt-4 border-y border-white/20">
            {slide.bullets.map((item, index) => (
              <li key={item} className="grid grid-cols-[52px_1fr] items-center gap-3 border-b border-white/20 py-3 last:border-b-0">
                <span className={`s2p-editorial text-3xl font-bold leading-none ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#5FC6D8]"}`}>0{index + 1}</span>
                <span className="text-base font-semibold leading-relaxed sm:text-lg lg:text-xl">{item}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  </div>
  );
};

interface SlideContentProps {
  slide: S2PElevateSlide;
  onStart: () => void;
  onGoToSlide: (id: string) => void;
}

const SlideContent = ({ slide, onStart, onGoToSlide }: SlideContentProps) => {
  const { program, config, renderSlide } = useS2PElevateDeck();
  const customSlide = renderSlide?.(slide);
  if (customSlide != null) return <>{customSlide}</>;
  if (config.analyticsVariant === "core" && slide.id === "privacy-team-rule") {
    return <GuidePromptSlide slide={ELEVATE_CHIEF_OF_STAFF_SLIDE} step={ELEVATE_CHIEF_OF_STAFF_STEP} trackingName="S2P Elevate AI" />;
  }
  const isLight = slide.tone === "paper" || slide.tone === "cloud";
  const showcaseItem = slide.showcaseId
    ? program.showcase.find((item) => item.id === slide.showcaseId)
    : undefined;

  if (slide.kind === "ai-foundation") return <ElevateFoundations slide={slide} learnerVoice={config.analyticsVariant === "core" || config.analyticsVariant === "rockline"} />;
  if (slide.kind === "record-skill") return <ElevateRecordSkill slide={slide} />;
  if (slide.kind === "agent-setup") return <ElevateAgentSetup key={slide.id} slide={slide} rockline={config.analyticsVariant === "rockline"} copyControl={slide.prompt && <PromptCopyButton label={slide.promptLabel ?? "Agent instructions"} value={slide.prompt} slideId={slide.id} />} />;
  if (slide.kind === "swot-library") return <ElevateSwotLibrary rockline={config.analyticsVariant === "rockline"} />;
  if (slide.kind === "tool-loop") return <div className="longhand-fundamentals w-full"><ToolLoop chiefOfStaff /></div>;

  if (slide.kind === "cover") return <CoverEditorialSlide slide={slide} onStart={onStart} />;

  if (slide.kind === "session") return <SessionEditorialSlide slide={slide} />;

  if (slide.id === "tool-to-team-thinking") return <ToolToTeamThinkingSlide slide={slide} />;

  if (slide.id === "ai-team-management-model") return <AITeamManagementModelSlide slide={slide} />;

  if (slide.id === "ai-team-ownership-question") return <AITeamOwnershipQuestionSlide slide={slide} />;

  if (slide.id === "chief-of-staff-operating-brief") return <ChiefOfStaffOperatingBriefSlide slide={slide} />;

  if (slide.id === "outcome-first-prompt-example") return <OutcomeFirstPromptExampleSlide slide={slide} />;

  if (slide.id === "iterate-with-good-examples" || (slide.id === "session-3-good-examples" && !program.slides.some((item) => item.id === "agent-create-schedule"))) return <GoodExamplesSlide slide={slide} />;

  if (slide.kind === "gear-system") {
    return <GearSystemSlide slide={slide} />;
  }

  if (slide.id === "privacy-data-boundary") {
    return <PrivacyBoundarySlide slide={slide} />;
  }

  if (slide.kind === "prompt-explorer") {
    return <PromptExplorerSlide slide={slide} />;
  }

  if (slide.kind === "decision-rights") {
    return <DecisionRightsSlide slide={slide} />;
  }

  if (slide.kind === "ease-impact") {
    return <EaseImpactSlide slide={slide} />;
  }

  if (slide.kind === "showcase-example" && showcaseItem) {
    return <ElevateShowcaseExample item={showcaseItem} slide={slide} practiceContent={showcaseItem.example?.practice && <PracticePanel key={slide.id} practice={showcaseItem.example.practice} slideId={slide.id} />} />;
  }

  if (slide.kind === "showcase-capability" && showcaseItem) {
    return <ShowcaseCapability item={showcaseItem} slide={slide} />;
  }

  if (slide.kind === "showcase-applications" && showcaseItem) {
    return <ShowcaseApplications item={showcaseItem} slide={slide} />;
  }

  if (slide.kind === "use-case-library") return <UseCaseLibrarySlide slide={slide} />;

  if (slide.kind === "outcomes") return <OutcomesEditorialSlide slide={slide} />;
  if (slide.kind === "compare") return <CompareEditorialSlide slide={slide} />;
  if (slide.kind === "prompt") return <PromptEditorialSlide slide={slide} />;
  if (slide.kind === "discussion") return <DiscussionEditorialSlide slide={slide} />;
  if (slide.kind === "activity") return <ActivityEditorialSlide slide={slide} />;
  if (slide.kind === "concept" || slide.kind === "levels") return <ConceptEditorialSlide slide={slide} />;
  if (slide.kind === "finish") return <FinishEditorialSlide slide={slide} onGoToSlide={onGoToSlide} />;

  return (
    <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-7 sm:py-10">
      {slide.eyebrow && <p className={`max-w-5xl text-xs font-extrabold uppercase tracking-[0.18em] ${isLight ? "text-[#0047FF]" : "text-[#9DB6FF]"}`}>{slide.eyebrow}</p>}
      <h1 className={`s2p-display mt-4 max-w-5xl text-4xl font-semibold uppercase leading-tight sm:text-5xl lg:text-6xl ${isLight ? "text-[#0A1F44]" : "text-white"}`}>{slide.title}</h1>
      {slide.lead && <p className={`mt-4 max-w-5xl text-base leading-relaxed sm:text-lg ${isLight ? "text-slate-600" : "text-slate-300"}`}>{slide.lead}</p>}

      {slide.quote && (
        <blockquote className={`mt-6 max-w-5xl border-l-4 border-[#0047FF] p-5 text-xl font-semibold leading-relaxed sm:text-2xl ${isLight ? "bg-[#E7EEFF] text-[#0A1F44]" : "bg-white/[0.06] text-white"}`}>
          {slide.quote}
        </blockquote>
      )}

      {slide.sections && <div className="mt-7"><EditorialSections sections={slide.sections} isLight={isLight} /></div>}

      {slide.bullets && (
        <ul className={`mt-7 grid gap-3 ${slide.bullets.length > 3 ? "md:grid-cols-2" : "max-w-5xl"}`}>
          {slide.bullets.map((bullet) => (
            <li key={bullet} className={`flex items-start gap-3 rounded-xl border p-4 ${isLight ? "border-[#E5E9F2] bg-white" : "border-white/15 bg-white/[0.06]"}`}>
              <CheckCircle2 className={`mt-0.5 h-5 w-5 flex-shrink-0 ${isLight ? "text-[#0047FF]" : "text-[#5FC6D8]"}`} />
              <span className={`leading-relaxed ${isLight ? "text-slate-700" : "text-slate-200"}`}>{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {slide.questions && (
        <ol className={`mt-7 grid gap-4 ${slide.id === "bounded-experiment-bridge" ? "lg:grid-cols-4" : slide.questions.length > 2 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
          {slide.questions.map((question, index) => (
            <li key={question} className={`flex items-start gap-4 rounded-2xl border p-5 ${isLight ? "border-[#E5E9F2] bg-white shadow-sm" : "border-white/15 bg-white/[0.06]"}`}>
              <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-sm font-extrabold ${isLight ? "bg-[#0047FF] text-white" : "bg-white text-[#0047FF]"}`}>{index + 1}</span>
              <span className={`pt-1 text-lg font-semibold leading-relaxed ${isLight ? "text-[#0A1F44]" : "text-white"}`}>{question}</span>
            </li>
          ))}
        </ol>
      )}

      {slide.prompt && (
        <div className="mt-7 max-w-5xl">
          <PromptPanel label={slide.promptLabel ?? "Copyable prompt"} value={slide.prompt} slideId={slide.id} />
        </div>
      )}

      {slide.note && <div className={`mt-5 max-w-5xl rounded-xl border-l-4 border-[#0047FF] p-4 text-sm leading-relaxed ${isLight ? "bg-[#E7EEFF] text-[#0A1F44]" : "bg-white/[0.06] text-slate-200"}`}><strong>Remember:</strong> {slide.note}</div>}
      {slide.warning && <div className={`mt-4 max-w-5xl rounded-xl border border-amber-400/40 p-4 text-sm leading-relaxed ${isLight ? "bg-amber-50 text-amber-950" : "bg-amber-400/10 text-amber-100"}`}><strong>Guardrail:</strong> {slide.warning}</div>}

      {shouldRenderSourceLink(slide) && (
        <a href={slide.sourceUrl!} target="_blank" rel="noopener noreferrer" className={`mt-5 inline-flex w-fit items-center gap-2 text-sm font-bold ${isLight ? "text-[#0047FF]" : "text-[#9DB6FF]"}`}>
          {slide.sourceLabel ?? "Open source"} <ExternalLink className="h-4 w-4" />
        </a>
      )}

    </div>
  );
};

const S2PElevateDeckContent = () => {
  const { program, config, renderContents, navigationSlideIds, navigationLabel } = useS2PElevateDeck();
  const brandKey = config.brand ?? "serve2perform";
  const brand = getDeckBrand(config);
  const isTechLeaders = brandKey === "tech-leaders";
  const S2P_ELEVATE_CURRICULUM = program.curriculum;
  const S2P_ELEVATE_SESSIONS = program.sessions;
  const S2P_ELEVATE_SLIDES = program.slides;
  const S2P_ELEVATE_TOTAL_MINUTES = program.totalMinutes;
  const SLIDE_STORAGE_KEY = `${config.storageNamespace}-current-slide`;
  const courseSchema = createCourseSchema(program, config);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [contentsOpen, setContentsOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const stageRef = useRef<HTMLElement>(null);
  const slideRef = useRef<HTMLElement>(null);
  const touchStart = useRef({ x: 0, y: 0 });
  const activeSlide = S2P_ELEVATE_SLIDES[activeSlideIndex];
  const activeVideo = S2P_ELEVATE_VIDEOS[activeSlide.id]
    ?? activeSlide.sourceIds.map((sourceId) => S2P_ELEVATE_VIDEOS[sourceId]).find(Boolean);
  const activeSession = S2P_ELEVATE_SESSIONS.find((session) => session.id === activeSlide.session)!;
  const slideProgress = Math.round(((activeSlideIndex + 1) / S2P_ELEVATE_SLIDES.length) * 100);
  const magicPromptSlideIndex = S2P_ELEVATE_SLIDES.findIndex((slide) => slide.id === MAGIC_PROMPT_SLIDE_ID);
  const hasMagicPrompt = magicPromptSlideIndex >= 0;
  const magicPromptHref = hasMagicPrompt ? `#${MAGIC_PROMPT_SLIDE_ID}` : config.magicPromptHref;
  const magicPromptLabel = hasMagicPrompt
    ? `Jump to the prompt template on slide ${magicPromptSlideIndex + 1}`
    : "Open the prompt template in the core deck";
  const brandHref = config.brandHref ?? (isTechLeaders ? "/ai-30/program" : S2P_ELEVATE_CURRICULUM.course.guideHref);
  const brandLinkLabel = config.brandLinkLabel ?? `Open ${brand.courseLabel}`;
  const slideTitleForAria = /[.!?]$/.test(activeSlide.title.trim())
    ? activeSlide.title.trim()
    : `${activeSlide.title.trim()}.`;

  useEffect(() => {
    try {
      const hashIndex = getSlideIndexFromHash(window.location.hash, S2P_ELEVATE_SLIDES);
      if (hashIndex >= 0) setActiveSlideIndex(hashIndex);
      else {
        const savedSlide = Number.parseInt(window.localStorage.getItem(SLIDE_STORAGE_KEY) ?? "", 10);
        if (Number.isFinite(savedSlide) && savedSlide >= 0 && savedSlide < S2P_ELEVATE_SLIDES.length) setActiveSlideIndex(savedSlide);
      }
    } catch {
      // The deck still opens at the first slide when saved position is unavailable.
    }
    trackEvent("S2P Elevate AI Page View", {
      total_minutes: S2P_ELEVATE_TOTAL_MINUTES,
      total_slides: S2P_ELEVATE_SLIDES.length,
      format: "five_session_slide_guide",
      program_variant: config.analyticsVariant,
    });
  }, [S2P_ELEVATE_SLIDES, S2P_ELEVATE_TOTAL_MINUTES, SLIDE_STORAGE_KEY, config.analyticsVariant]);

  const goToSlide = (index: number) => {
    const next = Math.max(0, Math.min(index, S2P_ELEVATE_SLIDES.length - 1));
    setVideoOpen(false);
    setActiveSlideIndex(next);
    try {
      window.localStorage.setItem(SLIDE_STORAGE_KEY, String(next));
      const slidePath = config.slideBasePath ? `${config.slideBasePath}${window.location.search}` : "";
      window.history.replaceState(null, "", deckSlideHref(S2P_ELEVATE_SLIDES[next].id, slidePath));
    } catch {
      // Navigation still works when storage or history is unavailable.
    }
  };

  const goToSlideId = (id: string) => {
    const index = S2P_ELEVATE_SLIDES.findIndex((slide) => slide.id === id);
    if (index >= 0) goToSlide(index);
    setContentsOpen(false);
  };

  const goToSession = (sessionId: number) => {
    const index = S2P_ELEVATE_SLIDES.findIndex((slide) => slide.session === sessionId);
    if (index >= 0) goToSlide(index);
    setContentsOpen(false);
  };

  const nextSlideIndex = adjacentDeckSlideIndex(S2P_ELEVATE_SLIDES, activeSlideIndex, 1, navigationSlideIds);
  const previousSlideIndex = adjacentDeckSlideIndex(S2P_ELEVATE_SLIDES, activeSlideIndex, -1, navigationSlideIds);
  const goNext = () => goToSlide(nextSlideIndex);
  const goPrevious = () => goToSlide(previousSlideIndex);
  const startWorkshop = () => goToSlide(adjacentDeckSlideIndex(S2P_ELEVATE_SLIDES, 0, 1, navigationSlideIds));

  const openVideo = () => {
    if (!activeVideo) return;
    setVideoOpen(true);
    trackEvent("S2P Elevate AI Related Video Opened", {
      program_variant: config.analyticsVariant,
      slide: activeSlide.id,
      video_id: activeVideo.youtubeId,
      video_title: activeVideo.title,
    });
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) await stageRef.current?.requestFullscreen();
    else await document.exitFullscreen();
  };

  useEffect(() => {
    slideRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeSlideIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (videoOpen) {
        if (event.key === "Escape") setVideoOpen(false);
        return;
      }
      if (contentsOpen) {
        if (event.key === "Escape") setContentsOpen(false);
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.closest("button, a, input, textarea, select, summary, [contenteditable]")) return;
      if (["ArrowRight", "PageDown", " "].includes(event.key)) {
        event.preventDefault();
        goNext();
      } else if (event.key === "ArrowLeft" || event.key === "PageUp") {
        event.preventDefault();
        goPrevious();
      } else if (event.key === "Home") {
        event.preventDefault();
        goToSlide(0);
      } else if (event.key === "End") {
        event.preventDefault();
        goToSlide(S2P_ELEVATE_SLIDES.length - 1);
      } else if (event.key.toLowerCase() === "f") void toggleFullscreen();
      else if (config.showContentsMenu !== false && (event.key.toLowerCase() === "c" || event.key === "?")) setContentsOpen(true);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  return (
    <div data-deck-brand={brandKey} className={`min-h-screen font-sans text-white ${isTechLeaders ? "bg-[#050505]" : "bg-[#07172F]"}`}>
      <SEO
        title={config.seoTitle}
        description={config.seoDescription}
        keywords={config.seoKeywords}
        robots={config.robots}
        structuredData={courseSchema}
      />
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@500;600;700&family=Oswald:wght@500;600;700&display=swap');
        .s2p-editorial { font-family: "Lora", Georgia, serif !important; }
        .s2p-display { font-family: "Oswald", "Arial Narrow", sans-serif !important; padding-bottom: 0 !important; }
        .s2p-fill-in-blank {
          display: inline-block;
          width: 2.25em;
          height: .72em;
          margin: 0 .08em;
          border-bottom: .09em solid currentColor;
          vertical-align: .08em;
        }
        .s2p-gear-number {
          font-family: Georgia, serif;
          font-weight: 700;
          text-anchor: middle;
          dominant-baseline: central;
          pointer-events: none;
        }
        .s2p-gear-rotor,
        .s2p-gear-mesh-rotor {
          transform-box: fill-box;
          transform-origin: center;
        }
        .s2p-gear-spinning .s2p-gear-rotor {
          animation: s2p-gear-turn 4.2s cubic-bezier(.22,.75,.2,1) var(--gear-delay,0s) both;
        }
        .s2p-gear-human { --gear-turn: 38deg; --gear-delay: 0s; --gear-mesh: 30deg; }
        .s2p-gear-chat { --gear-turn: -54deg; --gear-delay: .08s; --gear-mesh: -60deg; }
        .s2p-gear-assistant { --gear-turn: 72deg; --gear-delay: .18s; --gear-mesh: 60deg; }
        .s2p-gear-agents { --gear-turn: -94deg; --gear-delay: .3s; --gear-mesh: -90deg; }
        .s2p-gear-loops { --gear-turn: 124deg; --gear-delay: .44s; --gear-mesh: 120deg; }
        @keyframes s2p-gear-turn {
          from { transform: rotate(0deg); }
          to { transform: rotate(var(--gear-turn)); }
        }
        .s2p-gear-part { transition: opacity 180ms ease, filter 180ms ease; }
        .s2p-gear-system.is-linked .s2p-gear-part { opacity: .2; }
        .s2p-gear-system.is-linked .s2p-gear-part.is-lit { opacity: 1; }
        .s2p-gear-system.is-linked .s2p-gear-part.is-lit .s2p-gear-mesh-rotor {
          animation: s2p-gear-mesh 1.6s cubic-bezier(.24,.72,.3,1) both;
        }
        @keyframes s2p-gear-mesh {
          from { transform: rotate(0deg); }
          to { transform: rotate(var(--gear-mesh)); }
        }
        .s2p-gear-target { cursor: pointer; outline: none; }
        .s2p-gear-target:focus-visible { filter: drop-shadow(0 0 5px rgba(0,71,255,.8)); }
        .s2p-gear-mobile-row {
          border-color: color-mix(in srgb, var(--gear-accent) 58%, white);
          background: color-mix(in srgb, var(--gear-accent) 13%, white);
          transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
        }
        .s2p-gear-mobile-row:hover,
        .s2p-gear-mobile-row:focus-visible,
        .s2p-gear-mobile-row[aria-pressed="true"] {
          outline: none;
          border-color: var(--gear-ink);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--gear-accent) 24%, transparent);
          transform: translateY(-1px);
        }
        .s2p-prompt-segment {
          --segment-color: #fff;
          appearance: none;
          display: inline-flex;
          align-items: center;
          padding: .02em .09em .07em;
          border: 0;
          border-bottom: .035em solid color-mix(in srgb, var(--segment-color) 54%, transparent);
          border-radius: .12em;
          background: transparent;
          color: var(--segment-color);
          font: inherit;
          line-height: inherit;
          letter-spacing: inherit;
          cursor: help;
          transition: color 180ms ease, background 180ms ease, border-color 180ms ease, box-shadow 220ms ease, transform 220ms cubic-bezier(.2,.8,.2,1);
        }
        .s2p-prompt-segment[data-tone="orange"] { --segment-color: #FF7420; }
        .s2p-prompt-segment[data-tone="blue"] { --segment-color: #38BDF8; }
        .s2p-prompt-segment[data-tone="green"] { --segment-color: #34D399; }
        .s2p-prompt-segment[data-tone="yellow"] { --segment-color: #FBBF24; }
        .s2p-prompt-segment:hover,
        .s2p-prompt-segment:focus-visible,
        .s2p-prompt-segment[aria-expanded="true"] {
          outline: none;
          border-color: var(--segment-color);
          background: color-mix(in srgb, var(--segment-color) 13%, transparent);
          box-shadow: 0 .08em .32em color-mix(in srgb, var(--segment-color) 16%, transparent);
          transform: translateY(-.05em);
        }
        .s2p-prompt-segment:focus-visible {
          box-shadow: 0 0 0 3px #020817, 0 0 0 6px var(--segment-color), 0 .08em .32em color-mix(in srgb, var(--segment-color) 18%, transparent);
        }
        .s2p-prompt-detail {
          --detail-color: #fff;
          border: 1px solid color-mix(in srgb, var(--detail-color) 42%, transparent);
          border-left: 5px solid var(--detail-color);
          background: color-mix(in srgb, var(--detail-color) 9%, rgba(2,8,23,.88));
          box-shadow: 0 12px 32px rgba(0,0,0,.2);
        }
        .s2p-prompt-detail[data-tone="orange"] { --detail-color: #FF7420; }
        .s2p-prompt-detail[data-tone="blue"] { --detail-color: #38BDF8; }
        .s2p-prompt-detail[data-tone="green"] { --detail-color: #34D399; }
        .s2p-prompt-detail[data-tone="yellow"] { --detail-color: #FBBF24; }
        .s2p-prompt-detail-label { color: var(--detail-color); }
        .s2p-prompt-key::before {
          content: "";
          display: inline-block;
          width: .7em;
          height: .7em;
          margin-right: .45em;
          border-radius: 999px;
          background: currentColor;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-in { animation: none !important; }
          .s2p-gear-rotor,
          .s2p-gear-mesh-rotor { animation: none !important; }
          * { scroll-behavior: auto !important; transition-duration: 0.01ms !important; }
        }
        @media print { @page { size: landscape; margin: 0.4in; } }
      ` }} />

      <main ref={stageRef} className={`relative mx-auto flex h-[100svh] w-full max-w-[1920px] flex-col overflow-hidden ${isTechLeaders ? "bg-[#080808]" : "bg-[#0A1F44]"}`}>
        <header className="relative z-30 flex min-h-16 flex-shrink-0 items-center justify-between border-b border-[#E5E9F2] bg-white px-4 py-2 sm:px-6 lg:px-8">
          {brandHref ? (
            <a href={brandHref} aria-label={brandLinkLabel} className={`rounded-lg focus-visible:outline-none focus-visible:ring-2 ${isTechLeaders ? "focus-visible:ring-[#FF7420]" : "focus-visible:ring-[#0047FF]"}`}><Logo compact /></a>
          ) : (
            <Logo compact />
          )}
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden text-sm font-bold text-[#0A1F44] lg:inline">{config.sessionNoun ?? "Session"} {activeSession.id}: {activeSession.label}</span>
            {magicPromptHref && (
              <a
                href={magicPromptHref}
                onClick={(event) => {
                  if (!hasMagicPrompt) return;
                  event.preventDefault();
                  goToSlideId(MAGIC_PROMPT_SLIDE_ID);
                }}
                aria-label={magicPromptLabel}
                aria-current={activeSlide.id === MAGIC_PROMPT_SLIDE_ID ? "page" : undefined}
                className={`inline-flex min-h-10 items-center gap-1.5 rounded-full border px-2.5 text-xs font-extrabold focus-visible:outline-none focus-visible:ring-2 sm:gap-2 sm:px-4 sm:text-sm ${isTechLeaders ? "border-[#F4BE9D] bg-[#FCEBDE] text-[#9A3F0E] hover:border-[#FF7420] hover:bg-[#FAD9C4] focus-visible:ring-[#FF7420]" : "border-[#B8C9FF] bg-[#E7EEFF] text-[#0047FF] hover:border-[#0047FF] hover:bg-[#D7E2FF] focus-visible:ring-[#0047FF]"}`}
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                <span className="sm:hidden">Prompt</span>
                <span className="hidden sm:inline">Prompt template</span>
              </a>
            )}
            {activeVideo && (
              <button
                type="button"
                onClick={openVideo}
                aria-label={`Watch ${activeVideo.title}, ${activeVideo.duration}`}
                aria-expanded={videoOpen}
                className={`inline-flex min-h-10 items-center gap-1.5 rounded-full border px-2.5 text-xs font-extrabold focus-visible:outline-none focus-visible:ring-2 sm:gap-2 sm:px-4 sm:text-sm ${isTechLeaders ? "border-[#F4BE9D] bg-[#FCEBDE] text-[#9A3F0E] hover:border-[#FF7420] hover:bg-[#FAD9C4] focus-visible:ring-[#FF7420]" : "border-[#B8C9FF] bg-[#E7EEFF] text-[#0047FF] hover:border-[#0047FF] hover:bg-[#D7E2FF] focus-visible:ring-[#0047FF]"}`}
              >
                <PlayCircle className="h-4 w-4" aria-hidden="true" />
                <span className="sm:hidden">Watch</span>
                <span className="hidden sm:inline">Watch · {activeVideo.duration}</span>
              </button>
            )}
            <span className={`rounded-full px-3 py-1.5 text-xs font-extrabold sm:text-sm ${isTechLeaders ? "bg-[#FCEBDE] text-[#9A3F0E]" : "bg-[#E7EEFF] text-[#0047FF]"}`}>{activeSlideIndex + 1} / {S2P_ELEVATE_SLIDES.length}</span>
            {config.showContentsMenu !== false && <button type="button" onClick={() => setContentsOpen(true)} className={`inline-flex min-h-10 items-center gap-2 rounded-full border border-[#E5E9F2] px-3 text-sm font-bold text-[#0A1F44] focus-visible:outline-none focus-visible:ring-2 sm:px-4 ${isTechLeaders ? "hover:border-[#FF7420] hover:bg-[#FCEBDE] focus-visible:ring-[#FF7420]" : "hover:border-[#0047FF] hover:bg-[#E7EEFF] focus-visible:ring-[#0047FF]"}`} aria-label="Contents"><Menu className="h-4 w-4" /><span className="hidden sm:inline">Contents</span></button>}
          </div>
        </header>

        <section
          ref={slideRef}
          tabIndex={-1}
          aria-label={`${slideTitleForAria} Slide ${activeSlideIndex + 1} of ${S2P_ELEVATE_SLIDES.length}`}
          onTouchStart={(event) => { const touch = event.changedTouches[0]; touchStart.current = { x: touch.clientX, y: touch.clientY }; }}
          onTouchEnd={(event) => {
            const touch = event.changedTouches[0];
            const deltaX = touch.clientX - touchStart.current.x;
            const deltaY = touch.clientY - touchStart.current.y;
            if (Math.abs(deltaX) < 60 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
            if (deltaX < 0) goNext();
            else goPrevious();
          }}
          className={`relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 outline-none sm:px-8 lg:px-12 ${TONE_CLASSES[brandKey][activeSlide.tone]}`}
        >
          <div className="pointer-events-none absolute inset-0 opacity-50" aria-hidden="true" style={{
            backgroundImage: activeSlide.tone === "paper" || activeSlide.tone === "cloud"
              ? (isTechLeaders ? "radial-gradient(circle at 88% 16%, rgba(255,116,32,.08), transparent 25%), radial-gradient(circle at 8% 88%, rgba(31,42,68,.08), transparent 24%)" : "radial-gradient(circle at 88% 16%, rgba(0,71,255,.08), transparent 25%), radial-gradient(circle at 8% 88%, rgba(95,198,216,.08), transparent 24%)")
              : (isTechLeaders ? "radial-gradient(circle at 82% 18%, rgba(255,116,32,.12), transparent 28%), linear-gradient(115deg, transparent 60%, rgba(255,255,255,.035) 60% 62%, transparent 62% 68%, rgba(255,255,255,.035) 68% 70%, transparent 70%)" : "radial-gradient(circle at 82% 18%, rgba(157,182,255,.18), transparent 28%), linear-gradient(115deg, transparent 60%, rgba(255,255,255,.04) 60% 62%, transparent 62% 68%, rgba(255,255,255,.04) 68% 70%, transparent 70%)"),
          }} />
          <div key={activeSlide.id} className="relative h-full min-h-full animate-in fade-in slide-in-from-bottom-2 duration-300">
            <SlideContent slide={activeSlide} onStart={startWorkshop} onGoToSlide={goToSlideId} />
          </div>
        </section>

        {config.showNavigationFooter !== false && <footer className={`relative z-30 flex min-h-[76px] flex-shrink-0 items-center gap-3 border-t border-white/10 px-4 sm:px-6 lg:px-8 ${isTechLeaders ? "bg-[#080808]" : "bg-[#07172F]"}`}>
          <button type="button" onClick={goPrevious} disabled={previousSlideIndex === activeSlideIndex} aria-label="Previous slide" className={`inline-flex min-h-11 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-bold text-white hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-30 ${isTechLeaders ? "hover:border-[#FF7420] focus-visible:ring-[#FF7420]" : "hover:border-[#5FC6D8] focus-visible:ring-[#5FC6D8]"}`}><ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">Back</span></button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-4 text-xs"><span className="truncate font-semibold text-slate-400">{navigationLabel && `${navigationSlideIds && !navigationSlideIds.includes(activeSlide.id) ? "Optional reference" : navigationLabel} · `}{config.sessionNoun ?? "Session"} {activeSession.id} · {activeSlide.title}</span><span className={`font-bold ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#9DB6FF]"}`}>{slideProgress}%</span></div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10" aria-hidden="true"><div className={`h-full rounded-full bg-gradient-to-r transition-all duration-300 ${isTechLeaders ? "from-[#FF7420] to-[#F0A46A]" : "from-[#0047FF] to-[#5FC6D8]"}`} style={{ width: `${slideProgress}%` }} /></div>
          </div>
          {activeSlide.kind === "finish" ? (
            <a href={config.exitHref ?? brand.defaultExitHref} target={isTechLeaders ? undefined : "_blank"} rel={isTechLeaders ? undefined : "noopener noreferrer"} className={`inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-4 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 sm:px-5 ${isTechLeaders ? "text-[#1F2A44] hover:bg-[#FCEBDE] focus-visible:ring-[#FF7420]" : "text-[#0047FF] hover:bg-[#E7EEFF] focus-visible:ring-[#5FC6D8]"}`}><span className="hidden sm:inline">{config.exitLabel ?? brand.defaultExitLabel}</span><span className="sm:hidden">Exit</span><ExternalLink className="h-4 w-4" /></a>
          ) : (
            <button type="button" onClick={goNext} className={`inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 sm:px-5 ${isTechLeaders ? "bg-[#FF7420] hover:bg-[#E4691E] focus-visible:ring-[#FF9A5B]" : "bg-[#0047FF] hover:bg-[#0052FF] focus-visible:ring-[#5FC6D8]"}`}><span>Continue</span><ArrowRight className="h-4 w-4 flex-shrink-0" /></button>
          )}
        </footer>}

        {videoOpen && activeVideo && (
          <ProgramVideoDialog video={activeVideo} slideId={activeSlide.id} onClose={() => setVideoOpen(false)} />
        )}

        {config.showContentsMenu !== false && contentsOpen && (
          <div className={`absolute inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xl sm:p-8 ${isTechLeaders ? "bg-black/95" : "bg-[#07172F]/95"}`} role="dialog" aria-modal="true" aria-labelledby="s2p-contents-title" onMouseDown={(event) => { if (event.target === event.currentTarget) setContentsOpen(false); }}>
            <div className={`flex max-h-[90svh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-white/15 shadow-2xl shadow-black/50 ${isTechLeaders ? "bg-[#080808]" : "bg-[#0A1F44]"}`}>
              <div className="flex items-start justify-between gap-4 border-b border-white/10 p-5 sm:p-7">
                <div><p className={`text-xs font-extrabold uppercase tracking-[0.18em] ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#9DB6FF]"}`}>{config.contentsEyebrow ?? "Serve2Perform ELEVATE"}</p><h2 id="s2p-contents-title" className="s2p-display mt-2 text-3xl font-semibold uppercase text-white sm:text-4xl">{config.contentsTitle ?? "Five sessions"}</h2></div>
                <button type="button" onClick={() => setContentsOpen(false)} className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-white/15 text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 ${isTechLeaders ? "hover:border-[#FF7420] focus-visible:ring-[#FF7420]" : "hover:border-[#5FC6D8] focus-visible:ring-[#5FC6D8]"}`} aria-label="Close contents"><X className="h-5 w-5" /></button>
              </div>
              <div className="overflow-y-auto p-5 sm:p-7">
                {renderContents ? renderContents({ activeSlideId: activeSlide.id, goToSlideId }) : <div className="grid gap-5 lg:grid-cols-2">
                  {S2P_ELEVATE_SESSIONS.map((session) => (
                    <section key={session.id} className={`rounded-2xl border p-5 ${session.id === activeSession.id ? (isTechLeaders ? "border-[#FF7420] bg-white/[0.08]" : "border-[#5FC6D8] bg-white/[0.08]") : "border-white/10 bg-white/[0.04]"}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div><p className={`text-xs font-extrabold uppercase tracking-[0.15em] ${isTechLeaders ? "text-[#FF9A5B]" : "text-[#9DB6FF]"}`}>{config.sessionNoun ?? "Session"} {session.id}</p><h3 className="s2p-display mt-2 text-2xl font-semibold uppercase text-white">{session.label}</h3></div>
                        <button type="button" onClick={() => goToSession(session.id)} className={`inline-flex min-h-10 items-center gap-2 rounded-full bg-white px-4 text-sm font-bold ${isTechLeaders ? "text-[#1F2A44] hover:bg-[#FCEBDE]" : "text-[#0047FF] hover:bg-[#E7EEFF]"}`}><BookOpen className="h-4 w-4" /> Open</button>
                      </div>
                      <p className="mt-3 text-sm leading-relaxed text-slate-300">{session.outcome}</p>
                    </section>
                  ))}
                </div>}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export const S2PElevateDeck = ({
  program,
  config,
  renderSlide,
  renderContents,
  navigationSlideIds,
  navigationLabel,
}: {
  program: S2PElevateProgram;
  config: S2PElevateDeckConfig;
  renderSlide?: (slide: S2PElevateSlide) => ReactNode;
  renderContents?: (navigation: { activeSlideId: string; goToSlideId: (id: string) => void }) => ReactNode;
  navigationSlideIds?: readonly string[];
  navigationLabel?: string;
}) => (
  <S2PElevateDeckContext.Provider value={{ program, config, renderSlide, renderContents, navigationSlideIds, navigationLabel }}>
    <S2PElevateDeckContent />
  </S2PElevateDeckContext.Provider>
);

const S2PElevateAI = () => (
  <S2PElevateDeck
    program={S2P_ELEVATE_PROGRAM}
    config={{
      storageNamespace: "s2p-elevate-ai-v34",
      analyticsVariant: "core",
      showCoverLogo: false,
      seoTitle: "AI Tools & Workflows for Leaders: Serve2Perform ELEVATE",
      seoDescription: `A ${S2P_ELEVATE_PROGRAM.totalMinutes}-minute, five-session Serve2Perform ELEVATE workshop on complex prompting, choosing the right work and tool, controlling AI agents, and improving them with evidence.`,
      seoKeywords: ["Serve2Perform", "ELEVATE", "AI training for leaders", "AI prompting", "AI tool selection", "AI workflows", "AI agents", "Jobs to Be Done", "AI metric loops", "vibe coding"],
      finishCtaHref: "/s2p-elevate-ai-builder",
      finishCtaLabel: "Open the AI Builder Playbook",
    }}
  />
);

export default S2PElevateAI;
